import React, { useRef, useEffect, useState } from 'react';
import { Eraser, Brush, Undo, Redo, Trash2, ArrowRight, Loader2, Sparkles, Flower2, BedDouble, Download, Crosshair, Wand2, MapPin, LayoutDashboard, Car, Sun, TreeDeciduous, ChevronUp, ChevronDown, X, Cloud, Sunset, Home, Eye, Sofa, Armchair, Frame, Copy, Palette, Fence, Hammer } from 'lucide-react';
import Viewer360 from './Viewer360';
import BotanicalIcon from './BotanicalIcon';
import { 
  DEFAULT_BRUSH_SIZE, 
  MIN_BRUSH_SIZE, 
  MAX_BRUSH_SIZE, 
  MASK_COLOR, 
  COMPOSITE_MASK_COLOR,
  AUTO_DECLUTTER_PROMPT,
  GARDEN_DECLUTTER_PROMPT,
  DRIVEWAY_DECLUTTER_PROMPT,
  BINS_ONLY_PROMPT,
  CARS_ONLY_PROMPT,
  BED_DECLUTTER_PROMPT,
  BRIGHTEN_ENHANCE_PROMPT,
  BLUE_SKY_PROMPT_TEMPLATE,
  BLUE_SKY_TYPES,
  SUNSET_SKY_PROMPT,
  WINDOW_SKY_PROMPT,
  SUNSET_TYPES,
  ADD_FLOWERS_PROMPT,
  MANUAL_FLOWERS_PROMPT,
  PIN_FLOWERS_PROMPT,
  FLOWER_TYPES,
  PLANT_TYPES,
  ADD_PLANTS_PROMPT,
  MANUAL_PLANTS_PROMPT,
  PIN_PLANTS_PROMPT,
  MAX_PROCESSING_DIMENSION,
  GEMINI_INPUT_JPEG_QUALITY,
  CLEAR_FURNITURE_PROMPT,
  CLEAR_FURNITURE_360_PROMPT,
  STAGE_ROOM_PROMPT,
  STAGE_ROOM_360_PROMPT,
  FULL_RENOVATION_360_PROMPT,
  STAGE_OUTDOOR_360_PROMPT,
  REMOVE_TRIPOD_360_PROMPT,
  MATCH_STYLE_PROMPT,
  MATCH_STYLE_360_PROMPT,
  SAME_ROOM_ANGLE_PROMPT,
  SAME_ROOM_ANGLE_360_PROMPT,
  ROOM_TYPES,
  FURNITURE_STYLES,
  STAGING_TIERS,
  STAGING_FULL_RENOVATION_CREDITS,
  buildStagingStyleDescription,
  applyStagingTier,
  buildFullRenovationPrompt,
  ARTWORK_TYPES,
  ADD_ARTWORK_PROMPT,
  MANUAL_ARTWORK_PROMPT,
  PIN_ARTWORK_PROMPT,
  FURNITURE_ITEMS,
  ADD_FURNITURE_PROMPT,
  MANUAL_FURNITURE_PROMPT,
  PIN_FURNITURE_PROMPT,
  ACCEPTED_IMAGE_TYPES,
  COLOR_OPTIONS,
  COLOR_TARGETS,
  COLOR_SHELL_RULES,
  CHANGE_COLOR_PROMPT,
  MANUAL_CHANGE_COLOR_PROMPT,
  MANUAL_REMOVE_PROMPT,
  FIX_OPENINGS_PROMPT,
  OUTDOOR_SPACE_TYPES,
  OUTDOOR_STYLES,
  STAGE_OUTDOOR_PROMPT,
  applyUserInstructions,
} from '../constants';
import {
  getStagingImageModel,
  setStagingImageModel,
  STAGING_IMAGE_MODELS,
  type StagingImageModelId,
} from '../services/gemini';

function skyFanVars(
  index: number,
  count: number,
  { step = 50, arch = 36, maxAngle = 16, delayStep = 40 }: { step?: number; arch?: number; maxAngle?: number; delayStep?: number } = {},
) {
  const mid = (count - 1) / 2;
  const t = mid === 0 ? 0 : (index - mid) / mid;
  const angleDeg = t * maxAngle;
  return {
    ['--fan-x' as string]: `${((index - mid) * step).toFixed(1)}px`,
    ['--fan-y' as string]: `${(arch * (t * t - 1)).toFixed(1)}px`,
    ['--fan-rot' as string]: `${angleDeg.toFixed(2)}deg`,
    ['--fan-rot-neg' as string]: `${(-angleDeg).toFixed(2)}deg`,
    ['--fan-delay' as string]: `${index * delayStep}ms`,
    zIndex: Math.round(10 - Math.abs(index - mid)),
  };
}

function useDelayedOpen(open: boolean, delay = 80) {
  const [ready, setReady] = useState(false);
  useEffect(() => {
    if (!open) {
      setReady(false);
      return;
    }
    const id = window.setTimeout(() => setReady(true), delay);
    return () => window.clearTimeout(id);
  }, [open, delay]);
  return ready;
}

interface EditorProps {
  imageUrl: string;
  onProcess: (imageData: string, prompt: string, aspectRatio: string, restoreRect: {x: number, y: number, width: number, height: number} | null, is360?: boolean, referenceImageUrl?: string | null, maskImageData?: string | null, roomAnchorUrl?: string | null, creditCost?: number) => void;
  isProcessing: boolean;
  onDownload: () => void;
  onOpenDashboard: () => void;
  /**
   * When set, the user has a recently-processed result they backed out of.
   * Show a "Forward to result" affordance so they can return to it without
   * re-processing (and spending a generation).
   */
  onForwardToResult?: () => void;
  bundleGenerationsRemaining: number;
  bundleSize: number;
  bundleCost: number;
  /** When set, show "Match this image" and pass reference when processing. */
  referenceImageUrl?: string | null;
  /**
   * Same-room batch mode: `referenceImageUrl` is the high-resolution staged
   * "hero" angle of the SAME room (a layout ANCHOR, not a style swatch). The
   * match button stages this angle to reproduce the hero's furniture layout.
   */
  sameRoomMode?: boolean;
  onClearReference?: () => void;
  /** Allow setting reference from an uploaded file (data URL). */
  onSetReferenceImage?: (dataUrl: string) => void;
  selectedRoomType: string;
  selectedStyle: string;
  selectedStagingTier: string;
  onSelectedRoomTypeChange: (roomType: string) => void;
  onSelectedStyleChange: (style: string) => void;
  onSelectedStagingTierChange: (tier: string) => void;
  /**
   * Show the trial image-model picker (Flash / Pro / Sunburst).
   * Intended for local DEV and admins only — never for normal customers.
   */
  showModelPicker?: boolean;
}

interface HistoryStep {
  imageData: ImageData;
  hasMask: boolean;
}

const Editor: React.FC<EditorProps> = ({
  imageUrl,
  onProcess,
  isProcessing,
  onDownload,
  onOpenDashboard,
  onForwardToResult,
  bundleGenerationsRemaining,
  bundleSize,
  bundleCost,
  referenceImageUrl,
  sameRoomMode = false,
  onClearReference,
  onSetReferenceImage,
  selectedRoomType: initialSelectedRoomType,
  selectedStyle: initialSelectedStyle,
  selectedStagingTier: initialSelectedStagingTier,
  onSelectedRoomTypeChange,
  onSelectedStyleChange,
  onSelectedStagingTierChange,
  showModelPicker = false,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null); // Layer for drawing mask
  const imageRef = useRef<HTMLImageElement>(null); // Hidden image element
  
  // State
  const [isDrawing, setIsDrawing] = useState(false);
  const [brushSize, setBrushSize] = useState(DEFAULT_BRUSH_SIZE);
  const [mode, setMode] = useState<'brush' | 'eraser'>('brush');
  const [hasMask, setHasMask] = useState(false);
  const [customPrompt, setCustomPrompt] = useState("");
  const [canvasSize, setCanvasSize] = useState({ width: 0, height: 0 });
  const [selectedFlowerId, setSelectedFlowerId] = useState(FLOWER_TYPES[0].id);
  const [selectedPlantId, setSelectedPlantId] = useState(PLANT_TYPES[0].id);
  const [blueSkyFanOpen, setBlueSkyFanOpen] = useState(false);
  const [sunsetFanOpen, setSunsetFanOpen] = useState(false);
  const [flowerPickerOpen, setFlowerPickerOpen] = useState(false);
  const [plantPickerOpen, setPlantPickerOpen] = useState(false);
  const [selectedRoomType, setSelectedRoomType] = useState(initialSelectedRoomType);
  const [selectedStyle, setSelectedStyle] = useState(initialSelectedStyle);
  const [selectedStagingTier, setSelectedStagingTier] = useState(initialSelectedStagingTier);
  const [selectedArtworkId, setSelectedArtworkId] = useState(ARTWORK_TYPES[0].id);
  const [selectedFurnitureId, setSelectedFurnitureId] = useState(FURNITURE_ITEMS[0].id);
  const [selectedColorTarget, setSelectedColorTarget] = useState(COLOR_TARGETS[0].id);
  const [selectedColor, setSelectedColor] = useState(COLOR_OPTIONS[0].id);
  const [customColorText, setCustomColorText] = useState('');
  const [selectedOutdoorSpace, setSelectedOutdoorSpace] = useState(OUTDOOR_SPACE_TYPES[0].id);
  const [selectedOutdoorStyle, setSelectedOutdoorStyle] = useState(OUTDOOR_STYLES[0].id);
  const [trialModel, setTrialModel] = useState<StagingImageModelId>(() => getStagingImageModel());

  // Mobile bottom sheet state
  const [mobileSheetOpen, setMobileSheetOpen] = useState(false);
  const [mobileTab, setMobileTab] = useState<'quick' | 'custom'>('quick');
  
  // Processing action tracking for dynamic messages
  const [currentAction, setCurrentAction] = useState<string>('processing');
  
  // Pin Location State
  const [isPlacingPin, setIsPlacingPin] = useState(false);
  const [pinLocation, setPinLocation] = useState<{x: number, y: number} | null>(null);
  
  // 360 Image Detection
  const [is360Image, setIs360Image] = useState(false);
  const [show360Viewer, setShow360Viewer] = useState(false);
  const [showPanoramaBanner, setShowPanoramaBanner] = useState(true);
  // Free-text "extra instructions" for the next 360 staging pass — appended
  // to STAGE_ROOM_360 / STAGE_OUTDOOR_360 / MATCH_STYLE_360 by
  // applyUserInstructions. Cleared on each new image load (alongside the
  // other per-image state below).
  const [extraInstructions, setExtraInstructions] = useState('');

  // History for undo/redo (storing ImageData and mask state)
  const [history, setHistory] = useState<HistoryStep[]>([]);
  const [redoHistory, setRedoHistory] = useState<HistoryStep[]>([]);

  useEffect(() => {
    setSelectedRoomType(initialSelectedRoomType);
  }, [initialSelectedRoomType]);

  useEffect(() => {
    setSelectedStyle(initialSelectedStyle);
  }, [initialSelectedStyle]);

  useEffect(() => {
    setSelectedStagingTier(initialSelectedStagingTier);
  }, [initialSelectedStagingTier]);

  useEffect(() => {
    if (isProcessing) {
      setBlueSkyFanOpen(false);
      setSunsetFanOpen(false);
      setFlowerPickerOpen(false);
      setPlantPickerOpen(false);
    }
  }, [isProcessing]);

  useEffect(() => {
    if (!blueSkyFanOpen && !sunsetFanOpen && !flowerPickerOpen && !plantPickerOpen) return;
    const onPointerDown = (event: MouseEvent) => {
      const target = event.target as HTMLElement | null;
      if (target && !target.closest('[data-sky-fan]') && !target.closest('[data-botanical-picker]')) {
        setBlueSkyFanOpen(false);
        setSunsetFanOpen(false);
        setFlowerPickerOpen(false);
        setPlantPickerOpen(false);
      }
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setBlueSkyFanOpen(false);
        setSunsetFanOpen(false);
        setFlowerPickerOpen(false);
        setPlantPickerOpen(false);
      }
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [blueSkyFanOpen, sunsetFanOpen, flowerPickerOpen, plantPickerOpen]);

  // Initialize Canvas and handle Resize
  useEffect(() => {
    const img = new Image();
    img.src = imageUrl;
    
    const calculateSize = () => {
        if (containerRef.current && img.width > 0) {
            const containerW = containerRef.current.clientWidth;
            const containerH = containerRef.current.clientHeight;
            const imgAspect = img.width / img.height;
            const containerAspect = containerW / containerH;
    
            let renderW, renderH;
    
            if (imgAspect > containerAspect) {
              renderW = containerW;
              renderH = containerW / imgAspect;
            } else {
              renderH = containerH;
              renderW = containerH * imgAspect;
            }
    
            setCanvasSize({ width: renderW, height: renderH });
        }
    };

    img.onload = () => {
        imageRef.current = img;
        calculateSize();
        
        // Check if image is 360 equirectangular
        const ratio = img.width / img.height;
        const is360 = Math.abs(ratio - 2.0) < 0.1;
        setIs360Image(is360);
        // Re-show the panorama mode banner on each new image load.
        setShowPanoramaBanner(is360);
        
        // Reset state only on initial load, not resize
        if (history.length === 0) {
             setHistory([]);
             setRedoHistory([]);
             setHasMask(false);
             setCustomPrompt("");
             setExtraInstructions("");
             setIsDrawing(false);
             setPinLocation(null);
             setIsPlacingPin(false);
             const ctx = canvasRef.current?.getContext('2d');
             if (ctx && canvasRef.current) {
                  ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
             }
        }
    };

    window.addEventListener('resize', calculateSize);
    return () => window.removeEventListener('resize', calculateSize);
  }, [imageUrl]); // Dependency on imageUrl ensures reset on new image

  // Handle Canvas Resize (Sync Buffer)
  useEffect(() => {
    if (canvasRef.current && canvasSize.width > 0) {
      const canvas = canvasRef.current;
      const tempCtx = document.createElement('canvas').getContext('2d');
      let savedData = null;
      if (tempCtx && canvas.width > 0) {
          tempCtx.canvas.width = canvas.width;
          tempCtx.canvas.height = canvas.height;
          tempCtx.drawImage(canvas, 0, 0);
          savedData = tempCtx.canvas;
      }

      canvas.width = canvasSize.width;
      canvas.height = canvasSize.height;
      
      const ctx = canvas.getContext('2d');
      if (ctx && savedData) {
         ctx.drawImage(savedData, 0, 0, canvas.width, canvas.height);
      }
    }
  }, [canvasSize]);

  // Drawing Logic
  const getCoordinates = (e: React.MouseEvent | React.TouchEvent) => {
    if (!canvasRef.current) return { x: 0, y: 0 };
    const rect = canvasRef.current.getBoundingClientRect();
    const clientX = 'touches' in e ? e.touches[0].clientX : (e as React.MouseEvent).clientX;
    const clientY = 'touches' in e ? e.touches[0].clientY : (e as React.MouseEvent).clientY;
    return {
      x: clientX - rect.left,
      y: clientY - rect.top
    };
  };

  const handleCanvasMouseDown = (e: React.MouseEvent | React.TouchEvent) => {
    if (isProcessing) return;

    // Handle Pin Placement
    if (isPlacingPin) {
        const { x, y } = getCoordinates(e);
        // Store normalized coordinates
        if (canvasSize.width > 0 && canvasSize.height > 0) {
            setPinLocation({
                x: x / canvasSize.width,
                y: y / canvasSize.height
            });
            setIsPlacingPin(false);
        }
        return;
    }

    // Normal Drawing
    startDrawing(e);
  };

  const startDrawing = (e: React.MouseEvent | React.TouchEvent) => {
    setIsDrawing(true);
    const ctx = canvasRef.current?.getContext('2d');
    if (!ctx || !canvasRef.current) return;

    // If we start drawing, clear pin to avoid confusion
    if (pinLocation) setPinLocation(null);

    // Save history before new stroke
    const currentStep: HistoryStep = {
        imageData: ctx.getImageData(0, 0, canvasRef.current.width, canvasRef.current.height),
        hasMask: hasMask
    };
    setHistory(prev => [...prev.slice(-20), currentStep]); 
    setRedoHistory([]); 

    const { x, y } = getCoordinates(e);
    ctx.beginPath();
    ctx.moveTo(x, y);
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.lineWidth = brushSize;
    ctx.strokeStyle = mode === 'brush' ? MASK_COLOR : 'rgba(0,0,0,1)';
    
    if (mode === 'eraser') {
      ctx.globalCompositeOperation = 'destination-out';
    } else {
      ctx.globalCompositeOperation = 'source-over';
    }
  };

  const draw = (e: React.MouseEvent | React.TouchEvent) => {
    if (isPlacingPin) return; // Don't draw while placing pin
    if (!isDrawing || !canvasRef.current) return;
    e.preventDefault();
    const ctx = canvasRef.current.getContext('2d');
    if (!ctx) return;
    
    const { x, y } = getCoordinates(e);
    ctx.lineTo(x, y);
    ctx.stroke();
    
    if (!hasMask) setHasMask(true);
  };

  const stopDrawing = () => {
    if (isDrawing) {
      const ctx = canvasRef.current?.getContext('2d');
      ctx?.closePath();
      setIsDrawing(false);
      if (ctx) ctx.globalCompositeOperation = 'source-over';
    }
  };

  const handleUndo = () => {
    if (history.length === 0 || !canvasRef.current) return;
    const ctx = canvasRef.current.getContext('2d');
    if (!ctx) return;
    
    const currentStep: HistoryStep = {
        imageData: ctx.getImageData(0, 0, canvasRef.current.width, canvasRef.current.height),
        hasMask: hasMask
    };
    
    const previousStep = history[history.length - 1];
    
    ctx.putImageData(previousStep.imageData, 0, 0);
    setHasMask(previousStep.hasMask);
    
    setRedoHistory(prev => [...prev, currentStep]);
    setHistory(prev => prev.slice(0, -1));
  };

  const handleRedo = () => {
    if (redoHistory.length === 0 || !canvasRef.current) return;
    const ctx = canvasRef.current.getContext('2d');
    if (!ctx) return;

    const currentStep: HistoryStep = {
        imageData: ctx.getImageData(0, 0, canvasRef.current.width, canvasRef.current.height),
        hasMask: hasMask
    };

    const nextStep = redoHistory[redoHistory.length - 1];

    ctx.putImageData(nextStep.imageData, 0, 0);
    setHasMask(nextStep.hasMask);

    setHistory(prev => [...prev, currentStep]);
    setRedoHistory(prev => prev.slice(0, -1));
  };

  const handleClear = () => {
    if (!canvasRef.current) return;
    const ctx = canvasRef.current.getContext('2d');
    if (!ctx) return;
    
    const currentStep: HistoryStep = {
        imageData: ctx.getImageData(0, 0, canvasRef.current.width, canvasRef.current.height),
        hasMask: hasMask
    };
    setHistory(prev => [...prev.slice(-20), currentStep]);
    setRedoHistory([]); 
    
    ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
    setHasMask(false);
    setPinLocation(null);
  };

  const is360EquirectangularImage = (width: number, height: number): boolean => {
    const ratio = width / height;
    // 360 equirectangular images are 2:1 aspect ratio (tolerance of ±5%)
    return Math.abs(ratio - 2.0) < 0.1;
  };

  // Modified to support Pin Location and 360 images
  const prepareDataForProcessing = (includeMask: boolean, pinPos: {x: number, y: number} | null = null) => {
    if (!imageRef.current) return null;
    // The mask <canvas> is not rendered for 360° panoramas (brushing is hidden),
    // so only require it when we actually need to read a brushed mask.
    if (includeMask && !canvasRef.current) return null;

    const originalW = imageRef.current.width;
    const originalH = imageRef.current.height;
    
    // Check if this is a 360 equirectangular image
    const is360 = is360EquirectangularImage(originalW, originalH);
    
    // 360° equirectangular panoramas: send the source AS-IS to Gemini (no
    // padding, no crop). The `gemini-image` edge function maps unsupported
    // aspect ratios (`match_input_image`, `21:9`, etc.) to "no aspectRatio"
    // and lets Gemini infer the ratio from the input — so we don't need to
    // lie about the 2:1 shape.
    //
    // The previous "pad to 21:9" path silently cropped ~7% off the top and
    // bottom of the panorama (because 21:9 is wider than 2:1, so padding
    // to 21:9 with the same width REMOVES vertical pixels), which meant
    // Gemini never saw the ceiling/floor poles and produced flat-photo-
    // style staging instead of true equirectangular curvature. The full
    // rationale lives in App.tsx::prepareImageForAPI; see that comment
    // before changing this branch. The matching final 2:1 normalisation is
    // handled by App.tsx::cropResultToOriginal when `restoreRect == null`.
    //
    // Mask + pin tools are hidden in 360 mode in this editor, so this
    // branch never has to composite a mask. We keep the param surface
    // identical for the non-360 path below.
    if (is360) {
      let outW = originalW;
      let outH = originalH;
      if (outW > MAX_PROCESSING_DIMENSION) {
        outH = Math.round(outH * (MAX_PROCESSING_DIMENSION / outW));
        outW = MAX_PROCESSING_DIMENSION;
      }

      const outputCanvas = document.createElement('canvas');
      outputCanvas.width = outW;
      outputCanvas.height = outH;
      const ctx = outputCanvas.getContext('2d');
      if (!ctx) return null;

      const isScaling = outW !== originalW;
      ctx.imageSmoothingEnabled = isScaling;
      if (isScaling) ctx.imageSmoothingQuality = 'high';

      ctx.drawImage(imageRef.current, 0, 0, outW, outH);

      return {
        // High-quality JPEG (not PNG): a lossless 8192×4096 PNG data URL trips
        // the edge worker's limit (HTTP 546). No mask is composited in 360
        // mode, so JPEG is safe. See GEMINI_INPUT_JPEG_QUALITY.
        dataUrl: outputCanvas.toDataURL('image/jpeg', GEMINI_INPUT_JPEG_QUALITY),
        aspectRatioId: 'match_input_image',
        restoreRect: null,
        is360: true,
      };
    }

    // Non-360 images: send to Gemini AS-IS (no padding, no crop), with
    // `aspectRatioId = 'match_input_image'` so the `gemini-image` edge
    // function drops aspectRatio from imageConfig and Gemini infers the
    // ratio from the input image. See App.tsx::prepareImageForAPI for
    // the full rationale — short version: padding to one of Gemini's
    // enum aspect ratios with black bars caused the model to re-render
    // the room from a slightly different camera angle / FOV / framing
    // to "fill" the new aspect ratio, instead of doing a true in-place
    // edit. Removing the pad fixes that.
    let finalW = originalW;
    let finalH = originalH;
    let finalScale = 1;
    const longest = Math.max(finalW, finalH);
    if (longest > MAX_PROCESSING_DIMENSION) {
        finalScale = MAX_PROCESSING_DIMENSION / longest;
        finalW = Math.round(finalW * finalScale);
        finalH = Math.round(finalH * finalScale);
    }

    const outputCanvas = document.createElement('canvas');
    outputCanvas.width = finalW;
    outputCanvas.height = finalH;
    const ctx = outputCanvas.getContext('2d');
    if (!ctx) return null;

    const isScaling = finalScale !== 1;
    ctx.imageSmoothingEnabled = isScaling;
    if (isScaling) ctx.imageSmoothingQuality = 'high';

    ctx.drawImage(imageRef.current, 0, 0, finalW, finalH);

    if (includeMask || pinPos) {
        const maskCanvas = document.createElement('canvas');
        maskCanvas.width = finalW;
        maskCanvas.height = finalH;
        const mCtx = maskCanvas.getContext('2d');

        if (mCtx) {
            if (pinPos) {
                // Pin position is in normalised image coordinates [0,1].
                const pinX = pinPos.x * finalW;
                const pinY = pinPos.y * finalH;

                mCtx.fillStyle = COMPOSITE_MASK_COLOR;
                mCtx.beginPath();
                // 20px radius in original pixels, scaled to current canvas size.
                mCtx.arc(pinX, pinY, 20 * finalScale, 0, Math.PI * 2);
                mCtx.fill();
            } else {
                // Brush mask spans the full source image area now (no padding offsets).
                mCtx.drawImage(
                    canvasRef.current,
                    0, 0, canvasRef.current.width, canvasRef.current.height,
                    0, 0, finalW, finalH
                );

                mCtx.globalCompositeOperation = 'source-in';
                mCtx.fillStyle = COMPOSITE_MASK_COLOR;
                mCtx.fillRect(0, 0, finalW, finalH);
            }

            ctx.drawImage(maskCanvas, 0, 0);
        }
    }

    // Keep PNG (lossless) when a red mask / pin control overlay is burnt into
    // the image — JPEG bleed would soften that control signal. With no overlay
    // it's a plain scene, so use high-quality JPEG to keep the edge-function
    // payload under the worker limit (HTTP 546). See GEMINI_INPUT_JPEG_QUALITY.
    const hasControlOverlay = includeMask || !!pinPos;
    return {
        dataUrl: hasControlOverlay
          ? outputCanvas.toDataURL('image/png')
          : outputCanvas.toDataURL('image/jpeg', GEMINI_INPUT_JPEG_QUALITY),
        aspectRatioId: 'match_input_image',
        restoreRect: null,
        is360: false
    };
  };

  /**
   * Like {@link prepareDataForProcessing} but returns the source image WITHOUT
   * the red mask burnt in, plus a SEPARATE binary mask image (white-on-black)
   * at the same dimensions as the source we hand Gemini. Used by the
   * brush-based recolour flow so Gemini sees the actual source pixels and
   * the mask doesn't bleed into the output. Returns `null` if no brush mask
   * is present.
   *
   * The companion `maskOriginalDataUrl` is the mask re-rendered at the user's
   * ORIGINAL upload dimensions — the post-process mask blend uses it to
   * restore everything outside the brushed area pixel-perfectly.
   */
  const prepareSourceAndMaskForProcessing = () => {
    const clean = prepareDataForProcessing(false);
    if (!clean || !canvasRef.current || !imageRef.current) return null;
    if (!hasMask) return null;

    // Recreate the same canvas as `clean` but containing only the mask
    // (white-on-black). We re-derive the geometry so the mask aligns
    // pixel-for-pixel with the source.
    const originalW = imageRef.current.width;
    const originalH = imageRef.current.height;
    const is360 = is360EquirectangularImage(originalW, originalH);

    let finalW: number, finalH: number, scaledOffsetX: number, scaledOffsetY: number,
        scaledOriginalW: number, scaledOriginalH: number;

    if (is360) {
      // Mask/brush tools are hidden in 360° mode and `prepareSourceAndMask…`
      // early-returns when `!hasMask`, so this branch is currently dead
      // code. Kept correct for safety in case a future 360 mask flow is
      // added: matches the no-pad geometry of `prepareDataForProcessing`'s
      // 360 branch (see App.tsx::prepareImageForAPI for the full rationale).
      let outW = originalW;
      let outH = originalH;
      if (outW > MAX_PROCESSING_DIMENSION) {
        outH = Math.round(outH * (MAX_PROCESSING_DIMENSION / outW));
        outW = MAX_PROCESSING_DIMENSION;
      }
      finalW = outW;
      finalH = outH;
      scaledOriginalW = outW;
      scaledOriginalH = outH;
      scaledOffsetX = 0;
      scaledOffsetY = 0;
    } else {
      // Non-360 flat photos: no padding (matches the no-pad geometry of
      // `prepareDataForProcessing`'s non-360 branch above and
      // App.tsx::prepareImageForAPI). The mask spans the full image at
      // its native aspect ratio; only optional downscale to
      // MAX_PROCESSING_DIMENSION on the long edge.
      finalW = originalW;
      finalH = originalH;
      let finalScale = 1;
      const longest = Math.max(finalW, finalH);
      if (longest > MAX_PROCESSING_DIMENSION) {
        finalScale = MAX_PROCESSING_DIMENSION / longest;
        finalW = Math.round(finalW * finalScale);
        finalH = Math.round(finalH * finalScale);
      }
      scaledOriginalW = finalW;
      scaledOriginalH = finalH;
      scaledOffsetX = 0;
      scaledOffsetY = 0;
    }

    const maskCanvas = document.createElement('canvas');
    maskCanvas.width = finalW;
    maskCanvas.height = finalH;
    const mCtx = maskCanvas.getContext('2d');
    if (!mCtx) return null;
    mCtx.fillStyle = '#000000';
    mCtx.fillRect(0, 0, finalW, finalH);

    // Draw the user's brush mask, then collapse it to opaque white so Gemini
    // sees a clean binary signal rather than translucent red.
    mCtx.drawImage(
      canvasRef.current,
      0, 0, canvasRef.current.width, canvasRef.current.height,
      scaledOffsetX, scaledOffsetY, scaledOriginalW, scaledOriginalH
    );
    mCtx.globalCompositeOperation = 'source-in';
    mCtx.fillStyle = '#FFFFFF';
    mCtx.fillRect(0, 0, finalW, finalH);

    // Also produce a mask aligned to the user's ORIGINAL image dimensions
    // so the post-process composite can restore everything outside the brush.
    const maskOriginalCanvas = document.createElement('canvas');
    maskOriginalCanvas.width = originalW;
    maskOriginalCanvas.height = originalH;
    const moCtx = maskOriginalCanvas.getContext('2d');
    if (!moCtx) return null;
    moCtx.fillStyle = '#000000';
    moCtx.fillRect(0, 0, originalW, originalH);
    moCtx.drawImage(
      canvasRef.current,
      0, 0, canvasRef.current.width, canvasRef.current.height,
      0, 0, originalW, originalH
    );
    moCtx.globalCompositeOperation = 'source-in';
    moCtx.fillStyle = '#FFFFFF';
    moCtx.fillRect(0, 0, originalW, originalH);

    return {
      dataUrl: clean.dataUrl,
      maskDataUrl: maskCanvas.toDataURL('image/png'),
      maskOriginalDataUrl: maskOriginalCanvas.toDataURL('image/png'),
      aspectRatioId: clean.aspectRatioId,
      restoreRect: clean.restoreRect,
      is360: clean.is360,
    };
  };

  /**
   * The user's free-text request, merged from BOTH instruction boxes:
   * the "Custom Request" box (`customPrompt`) and the "Additional Context"
   * box (`extraInstructions`). Either box now feeds every staging /
   * renovation / outdoor / match action, so a request is never silently
   * dropped just because it was typed in the "wrong" box. Structural rules
   * still win (see `applyUserInstructions`).
   */
  const combinedUserInstructions = (): string =>
    [extraInstructions, customPrompt]
      .map((s) => s.trim())
      .filter(Boolean)
      .join('\n');

  /**
   * For 360° equirectangular inputs, swap the prompt for an equirectangular-aware
   * variant (or prepend a short panorama note when no dedicated variant exists).
   * Substitutions ({{ROOM_TYPE}}, {{STYLE}}, {{SPACE_TYPE}}) are preserved by
   * extracting them from the already-rendered prompt and re-applying to the 360
   * template.
   */
  const PANORAMA_NOTE =
    'IMPORTANT: This is a 360° equirectangular panorama (2:1 aspect ratio). The left and right edges MUST wrap seamlessly. Vertical lines bend horizontally near the floor and ceiling — account for equirectangular projection distortion. Do NOT change the image dimensions, projection, or seamless wrap. ';

  const toPanoramaPrompt = (prompt: string): string => {
    if (!is360Image) return prompt;

    // Recognise the canonical Stage Room template and swap to the 360 variant.
    // NOTE: the flat STAGE_ROOM_PROMPT lead is "...add {{STYLE}} furniture and
    // movable decor ... so the room reads as a {{ROOM_TYPE}}." — so here the
    // FIRST capture group is the STYLE and the SECOND is the ROOM_TYPE.
    const stageRoomMatch = prompt.match(
      /Take the attached photograph and add (.+?) furniture and movable decor into its empty floor space so the room reads as a (.+?)\./
    );
    if (stageRoomMatch) {
      return applyUserInstructions(
        STAGE_ROOM_360_PROMPT
          .replace('{{ROOM_TYPE}}', stageRoomMatch[2])
          .replace('{{STYLE}}', stageRoomMatch[1]),
        combinedUserInstructions(),
      );
    }

    const renovationMatch = prompt.match(
      /FULL RENOVATION of this unfinished room\. Target: (.+?)\. Style: (.+?)\./
    );
    if (renovationMatch) {
      const briefMatch = prompt.match(/ROOM-SPECIFIC FIT-OUT:\n([\s\S]+?)\n\nTHEN FURNISH/);
      return applyUserInstructions(
        FULL_RENOVATION_360_PROMPT
          .replace(/\{\{ROOM_TYPE\}\}/g, renovationMatch[1])
          .replace(/\{\{STYLE\}\}/g, renovationMatch[2])
          .replace(/\{\{FINISH_BRIEF\}\}/g, (briefMatch?.[1] || '').trim()),
        combinedUserInstructions(),
      );
    }

    // Recognise the canonical Stage Outdoor template.
    const stageOutdoorMatch = prompt.match(
      /Virtually stage this outdoor space as (.+?) with (.+?)\.\n/
    );
    if (stageOutdoorMatch) {
      return applyUserInstructions(
        STAGE_OUTDOOR_360_PROMPT
          .replace('{{SPACE_TYPE}}', stageOutdoorMatch[1])
          .replace('{{STYLE}}', stageOutdoorMatch[2]),
        combinedUserInstructions(),
      );
    }

    // Recognise the canonical Clear Furniture prompt and swap to the strict
    // 360 variant (which preserves camera position, horizon, projection, and
    // every architectural element of the panorama). Clear-furniture is a
    // structural removal — extra user instructions don't apply, so we don't
    // wrap it.
    if (prompt === CLEAR_FURNITURE_PROMPT) {
      return CLEAR_FURNITURE_360_PROMPT;
    }

    // Anything else (custom prompt, etc.): prepend the note so Gemini still
    // respects equirectangular geometry.
    return PANORAMA_NOTE + prompt;
  };

  const handleAutoProcess = (prompt: string, actionType: string = 'processing', creditCost?: number) => {
    if (isProcessing) return;
    setCurrentAction(actionType);
    // UPDATED: Now respects user selection (hasMask) if available
    const result = prepareDataForProcessing(hasMask);
    if (result) {
        let finalPrompt = toPanoramaPrompt(prompt);
        // Layer the user's extra staging context on top of the staging prompt.
        // In 360° mode this is already applied inside toPanoramaPrompt, so only
        // wrap here for the regular (flat photo) flow to avoid double-applying.
        if (!is360Image && (actionType === 'stage' || actionType === 'clear' || actionType === 'renovate' || actionType === 'outdoor')) {
            finalPrompt = applyUserInstructions(finalPrompt, combinedUserInstructions());
        }
        onProcess(result.dataUrl, finalPrompt, result.aspectRatioId, result.restoreRect, result.is360, undefined, undefined, undefined, creditCost);
    }
  };

  const handleFullRenovation = () => {
    const roomType = ROOM_TYPES.find(r => r.id === selectedRoomType);
    const prompt = buildFullRenovationPrompt(
      selectedRoomType,
      buildStagingStyleDescription(selectedStyle, selectedStagingTier),
      roomType?.label || 'Living Room',
    );
    handleAutoProcess(prompt, 'renovate', STAGING_FULL_RENOVATION_CREDITS);
  };

  const handleMatchStyleProcess = () => {
    if (isProcessing || !referenceImageUrl) return;
    setCurrentAction('stage');
    const result = prepareDataForProcessing(false);
    if (result) {
      if (sameRoomMode) {
        // Same-room batch: reference is the high-res staged hero. Use the
        // layout-preserving anchor prompt and pass it as the room anchor
        // (8th arg) so App routes to the high-res anchor flow, NOT the
        // low-res style-swatch flow.
        const anchorPrompt = applyUserInstructions(
          is360Image ? SAME_ROOM_ANGLE_360_PROMPT : SAME_ROOM_ANGLE_PROMPT,
          combinedUserInstructions(),
        );
        onProcess(result.dataUrl, anchorPrompt, result.aspectRatioId, result.restoreRect, result.is360, null, null, referenceImageUrl);
        return;
      }
      const matchPrompt = applyUserInstructions(
        is360Image ? MATCH_STYLE_360_PROMPT : MATCH_STYLE_PROMPT,
        combinedUserInstructions(),
      );
      onProcess(result.dataUrl, matchPrompt, result.aspectRatioId, result.restoreRect, result.is360, referenceImageUrl);
    }
  };

  const handleManualProcess = () => {
    if (isProcessing) return;

    let promptToUse = customPrompt.trim();
    
    // Default removal behavior if mask exists but no prompt: route through the
    // pixel-locked mask flow so only the painted area changes (walls /
    // perspective / the rest of the staging stay identical).
    if (hasMask && !promptToUse) {
        handleRemoveSelection();
        return;
    }
    setCurrentAction('custom');

    // Require prompt if no mask (Global Edit)
    if (!hasMask && !promptToUse) {
        return;
    }

    const result = prepareDataForProcessing(hasMask);
    if (result) {
        onProcess(result.dataUrl, promptToUse, result.aspectRatioId, result.restoreRect, result.is360);
    }
  };

  const handleAddFlowers = () => {
    if (isProcessing) return;
    
    const flower = FLOWER_TYPES.find(f => f.id === selectedFlowerId) || FLOWER_TYPES[0];
    const description = flower.description;

    if (pinLocation) {
        // Use Pin Logic
        const result = prepareDataForProcessing(false, pinLocation);
        if (result) {
            const prompt = PIN_FLOWERS_PROMPT.replace('{{FLOWERS}}', description);
            onProcess(result.dataUrl, prompt, result.aspectRatioId, result.restoreRect, result.is360);
        }
    } else if (hasMask) {
        // Use Manual Mask Logic
        const result = prepareDataForProcessing(true);
        if (result) {
            const prompt = MANUAL_FLOWERS_PROMPT.replace('{{FLOWERS}}', description);
            onProcess(result.dataUrl, prompt, result.aspectRatioId, result.restoreRect, result.is360);
        }
    } else {
        // Use Auto Logic
        const prompt = ADD_FLOWERS_PROMPT.replace('{{FLOWERS}}', description);
        handleAutoProcess(prompt);
    }
  };

  const handleAddPlants = () => {
    if (isProcessing) return;
    
    const plant = PLANT_TYPES.find(p => p.id === selectedPlantId) || PLANT_TYPES[0];
    const description = plant.description;

    if (pinLocation) {
        // Use Pin Logic
        const result = prepareDataForProcessing(false, pinLocation);
        if (result) {
            const prompt = PIN_PLANTS_PROMPT.replace('{{PLANT}}', description);
            onProcess(result.dataUrl, prompt, result.aspectRatioId, result.restoreRect, result.is360);
        }
    } else if (hasMask) {
        // Use Manual Mask Logic
        const result = prepareDataForProcessing(true);
        if (result) {
            const prompt = MANUAL_PLANTS_PROMPT.replace('{{PLANT}}', description);
            onProcess(result.dataUrl, prompt, result.aspectRatioId, result.restoreRect, result.is360);
        }
    } else {
        // Use Auto Logic
        const prompt = ADD_PLANTS_PROMPT.replace('{{PLANT}}', description);
        handleAutoProcess(prompt);
    }
  };

  const handleSunsetSky = (sunsetId: string) => {
    if (isProcessing) return;
    const sunset = SUNSET_TYPES.find(s => s.id === sunsetId) || SUNSET_TYPES[0];
    const prompt = SUNSET_SKY_PROMPT.replace('{{SUNSET_TYPE}}', sunset.description);
    setSunsetFanOpen(false);
    handleAutoProcess(prompt, 'sky');
  };

  const handleBlueSky = (skyId: string) => {
    if (isProcessing) return;
    const sky = BLUE_SKY_TYPES.find((type) => type.id === skyId) || BLUE_SKY_TYPES[1];
    const prompt = BLUE_SKY_PROMPT_TEMPLATE.replace('{{SKY_TYPE}}', sky.description);
    setBlueSkyFanOpen(false);
    handleAutoProcess(prompt, 'sky');
  };

const handleAddArtwork = () => {
    if (isProcessing) return;
    setCurrentAction('custom');
    
    const artwork = ARTWORK_TYPES.find(a => a.id === selectedArtworkId) || ARTWORK_TYPES[0];
    const description = artwork.description;

    if (pinLocation) {
        // Use Pin Logic - place at specific wall location
        const result = prepareDataForProcessing(false, pinLocation);
        if (result) {
            const prompt = PIN_ARTWORK_PROMPT.replace('{{ARTWORK}}', description);
            onProcess(result.dataUrl, prompt, result.aspectRatioId, result.restoreRect, result.is360);
        }
    } else if (hasMask) {
        // Use Manual Mask Logic - place on marked wall area
        const result = prepareDataForProcessing(true);
        if (result) {
            const prompt = MANUAL_ARTWORK_PROMPT.replace('{{ARTWORK}}', description);
            onProcess(result.dataUrl, prompt, result.aspectRatioId, result.restoreRect, result.is360);
        }
    } else {
        // Use Auto Logic - AI finds best wall placement
        const prompt = ADD_ARTWORK_PROMPT.replace('{{ARTWORK}}', description);
        handleAutoProcess(prompt);
    }
  };

  const handleAddFurniture = () => {
    if (isProcessing) return;
    setCurrentAction('custom');
    
    const furniture = FURNITURE_ITEMS.find(f => f.id === selectedFurnitureId) || FURNITURE_ITEMS[0];
    const description = furniture.description;

    if (pinLocation) {
        // Use Pin Logic - place at specific floor location
        const result = prepareDataForProcessing(false, pinLocation);
        if (result) {
            const prompt = PIN_FURNITURE_PROMPT.replace('{{FURNITURE}}', description);
            onProcess(result.dataUrl, prompt, result.aspectRatioId, result.restoreRect, result.is360);
        }
    } else if (hasMask) {
        // Use Manual Mask Logic - place in marked area
        const result = prepareDataForProcessing(true);
        if (result) {
            const prompt = MANUAL_FURNITURE_PROMPT.replace('{{FURNITURE}}', description);
            onProcess(result.dataUrl, prompt, result.aspectRatioId, result.restoreRect, result.is360);
        }
    } else {
        // Use Auto Logic - AI finds best placement
        const prompt = ADD_FURNITURE_PROMPT.replace('{{FURNITURE}}', description);
        handleAutoProcess(prompt);
    }
  };

  /**
   * Brush-based removal that PIXEL-LOCKS everything outside the painted area.
   *
   * Unlike the old red-overlay removal (which re-rendered the whole photo via
   * declutterImage and could drift the walls/perspective), this sends a CLEAN
   * source plus a SEPARATE binary mask and routes to editImageWithMask +
   * compositeMaskedResult. Only the painted region is regenerated; the rest of
   * the image is restored byte-for-byte from the image the user was editing.
   * Works the same whether the user is on the original upload or iterating on
   * a staged result via "Continue Editing".
   */
  const handleRemoveSelection = () => {
    if (isProcessing || !hasMask) return;
    setCurrentAction('remove');
    const result = prepareSourceAndMaskForProcessing();
    if (result) {
      onProcess(
        result.dataUrl,
        MANUAL_REMOVE_PROMPT,
        result.aspectRatioId,
        result.restoreRect,
        result.is360,
        undefined,
        result.maskDataUrl,
      );
    }
  };

  /**
   * Brush over an invented door/wall after Full Renovation. Uses the original
   * upload (kept in App as originalFile) as the architecture reference so the
   * opening is restored without re-running the whole renovation.
   */
  const handleFixOpenings = () => {
    if (isProcessing || !hasMask || is360Image) return;
    setCurrentAction('fix-openings');
    const result = prepareSourceAndMaskForProcessing();
    if (result) {
      onProcess(
        result.dataUrl,
        FIX_OPENINGS_PROMPT,
        result.aspectRatioId,
        result.restoreRect,
        result.is360,
        undefined,
        result.maskDataUrl,
      );
    }
  };

  const handleChangeColor = () => {
    if (isProcessing) return;
    const colorDescription = selectedColor === 'custom'
      ? customColorText.trim()
      : (COLOR_OPTIONS.find(c => c.id === selectedColor) || COLOR_OPTIONS[0]).description;
    if (!colorDescription) return;

    if (selectedColorTarget === 'brush-selection') {
      if (!hasMask) return;
      // Send the source WITHOUT the red mask burnt in plus a separate binary
      // mask. This stops Gemini from baking the red brush stroke into the
      // result and lets it actually see the underlying surface to recolour.
      const result = prepareSourceAndMaskForProcessing();
      if (result) {
        const prompt = MANUAL_CHANGE_COLOR_PROMPT.replace('{{COLOR}}', colorDescription);
        onProcess(result.dataUrl, prompt, result.aspectRatioId, result.restoreRect, result.is360, undefined, result.maskDataUrl);
      }
    } else {
      const target = COLOR_TARGETS.find(t => t.id === selectedColorTarget) || COLOR_TARGETS[1];
      const prompt = CHANGE_COLOR_PROMPT
        .replace('{{TARGET}}', target.description)
        .replace('{{COLOR}}', colorDescription)
        .replace('{{SHELL_RULE}}', COLOR_SHELL_RULES[selectedColorTarget] ?? COLOR_SHELL_RULES.default);
      handleAutoProcess(prompt, 'recolor');
    }
  };

  const handleStageOutdoor = () => {
    if (isProcessing) return;
    setCurrentAction('outdoor');
    const space = OUTDOOR_SPACE_TYPES.find(s => s.id === selectedOutdoorSpace) || OUTDOOR_SPACE_TYPES[0];
    const style = OUTDOOR_STYLES.find(s => s.id === selectedOutdoorStyle) || OUTDOOR_STYLES[0];
    const prompt = STAGE_OUTDOOR_PROMPT
      .replace('{{SPACE_TYPE}}', space.description)
      .replace('{{STYLE}}', applyStagingTier(style.description, selectedStagingTier));
    handleAutoProcess(prompt, 'outdoor');
  };

  const BlueSkyFanControl = ({ compact = false }: { compact?: boolean }) => {
    const fanReady = useDelayedOpen(blueSkyFanOpen, 70);
    return (
      <div
        data-sky-fan
        className={`
          rounded-xl border transition-all
          ${blueSkyFanOpen ? 'overflow-visible' : 'overflow-hidden'}
          ${isProcessing
            ? 'bg-slate-800 border-slate-700 opacity-50 cursor-not-allowed'
            : blueSkyFanOpen
            ? 'bg-slate-800 border-sky-500/60 shadow-md shadow-sky-900/20'
            : 'bg-slate-800 border-slate-700 hover:border-sky-500/50 hover:shadow-md'
          }
        `}
      >
        <button
          type="button"
          onClick={() => {
            if (isProcessing) return;
            setSunsetFanOpen(false);
            setFlowerPickerOpen(false);
            setPlantPickerOpen(false);
            setBlueSkyFanOpen((open) => !open);
          }}
          disabled={isProcessing}
          className={`w-full text-left flex items-center gap-3 hover:bg-slate-700/50 transition-colors ${compact ? 'py-2.5 px-3' : 'py-3 px-4'}`}
        >
          <div className="p-1.5 bg-sky-900/30 rounded-lg text-sky-400 shrink-0">
            <Cloud size={compact ? 18 : 16} />
          </div>
          <div className="flex flex-col items-start flex-1 min-w-0">
            <span className={`font-medium ${compact ? 'text-xs text-sky-100' : 'text-sm text-sky-200'}`}>
              {isProcessing ? 'Processing...' : 'Blue Sky'}
            </span>
            <span className="text-[10px] text-sky-400/70 leading-none mt-0.5">
              {blueSkyFanOpen ? 'Choose a sky style' : 'Replace exterior sky'}
            </span>
          </div>
          <ChevronDown
            size={16}
            className={`text-sky-400/70 shrink-0 transition-transform duration-300 ${blueSkyFanOpen ? 'rotate-180' : ''}`}
          />
        </button>

        <div className={`sky-fan ${blueSkyFanOpen ? 'sky-fan-open' : ''} ${fanReady ? 'sky-fan-spread' : ''}`}>
          <div className="sky-fan-inner">
            <div className="sky-fan-stage">
              {BLUE_SKY_TYPES.map((type, index) => (
                <button
                  key={type.id}
                  type="button"
                  disabled={isProcessing || !fanReady}
                  onClick={() => {
                    handleBlueSky(type.id);
                    if (compact) setMobileSheetOpen(false);
                  }}
                  className="sky-fan-swatch"
                  style={skyFanVars(index, BLUE_SKY_TYPES.length, {
                    step: compact ? 44 : 50,
                    arch: compact ? 24 : 28,
                    maxAngle: 14,
                    delayStep: 45,
                  })}
                  title={type.label}
                >
                  <span className="sky-fan-disc" style={{ background: type.swatch }} />
                  <span className="sky-fan-label">{type.label}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const SunsetFanControl = ({ compact = false }: { compact?: boolean }) => {
    const fanReady = useDelayedOpen(sunsetFanOpen, 70);
    return (
      <div
        data-sky-fan
        className={`
          sky-fan-sunset rounded-xl border transition-all
          ${sunsetFanOpen ? 'overflow-visible -mx-4' : 'overflow-hidden'}
          ${isProcessing
            ? 'bg-slate-800 border-slate-700 opacity-50 cursor-not-allowed'
            : sunsetFanOpen
            ? 'bg-slate-800 border-orange-500/60 shadow-md shadow-orange-900/20'
            : 'bg-slate-800 border-slate-700 hover:border-orange-500/50 hover:shadow-md'
          }
        `}
      >
        <button
          type="button"
          onClick={() => {
            if (isProcessing) return;
            setBlueSkyFanOpen(false);
            setFlowerPickerOpen(false);
            setPlantPickerOpen(false);
            setSunsetFanOpen((open) => !open);
          }}
          disabled={isProcessing}
          className={`w-full text-left flex items-center gap-3 hover:bg-slate-700/50 transition-colors ${compact ? 'py-2.5 px-3' : 'py-3 px-4'}`}
        >
          <div className="p-1.5 bg-orange-900/30 rounded-lg text-orange-400 shrink-0">
            <Sunset size={compact ? 18 : 16} />
          </div>
          <div className="flex flex-col items-start flex-1 min-w-0">
            <span className={`font-medium ${compact ? 'text-xs text-orange-100' : 'text-sm text-orange-200'}`}>
              {isProcessing ? 'Processing...' : 'Sunset Sky'}
            </span>
            <span className="text-[10px] text-orange-400/70 leading-none mt-0.5">
              {sunsetFanOpen ? 'Choose a sunset style' : 'Replace with dramatic sunset'}
            </span>
          </div>
          <ChevronDown
            size={16}
            className={`text-orange-400/70 shrink-0 transition-transform duration-300 ${sunsetFanOpen ? 'rotate-180' : ''}`}
          />
        </button>

        <div className={`sky-fan ${sunsetFanOpen ? 'sky-fan-open' : ''} ${fanReady ? 'sky-fan-spread' : ''}`}>
          <div className="sky-fan-inner">
            <div className="sky-fan-stage">
              {SUNSET_TYPES.map((type, index) => (
                <button
                  key={type.id}
                  type="button"
                  disabled={isProcessing || !fanReady}
                  onClick={() => {
                    handleSunsetSky(type.id);
                    if (compact) setMobileSheetOpen(false);
                  }}
                  className="sky-fan-swatch"
                  style={skyFanVars(index, SUNSET_TYPES.length, {
                    step: compact ? 46 : 50,
                    arch: compact ? 34 : 40,
                    maxAngle: 16,
                    delayStep: 40,
                  })}
                  title={type.label}
                >
                  <span className="sky-fan-disc" style={{ background: type.swatch }} />
                  <span className="sky-fan-label">{type.label}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const BotanicalSwatchGrid = ({
    types,
    selectedId,
    onSelect,
    accent,
    open,
  }: {
    types: { id: string; label: string; swatch: string }[];
    selectedId: string;
    onSelect: (id: string) => void;
    accent: 'pink' | 'emerald';
    open: boolean;
  }) => {
    const animate = useDelayedOpen(open, 90);
    return (
    <div className={`botanical-grid ${animate ? 'botanical-grid-open' : ''}`}>
      {types.map((type, index) => (
        <button
          key={type.id}
          type="button"
          title={type.label}
          disabled={isProcessing || !open}
          onClick={(e) => {
            e.stopPropagation();
            onSelect(type.id);
          }}
          className={`botanical-swatch botanical-swatch-${accent} ${selectedId === type.id ? 'botanical-swatch-selected' : ''}`}
          style={{ ['--swatch-delay' as string]: `${index * 28}ms` }}
        >
          <span className="botanical-disc" style={{ background: type.swatch }}>
            <BotanicalIcon id={type.id} />
          </span>
        </button>
      ))}
    </div>
    );
  };

  // Mobile action button component for cleaner code
  const MobileActionButton = ({ onClick, icon: Icon, label, color = 'orange' }: { onClick: () => void, icon: any, label: string, color?: string }) => {
    const colorClasses: Record<string, string> = {
      orange: 'bg-orange-900/30 text-orange-400',
      green: 'bg-green-900/30 text-green-400',
      rose: 'bg-rose-900/30 text-rose-400',
      blue: 'bg-blue-900/30 text-blue-400',
      yellow: 'bg-yellow-900/30 text-yellow-400',
      pink: 'bg-pink-900/30 text-pink-400',
      emerald: 'bg-emerald-900/30 text-emerald-400',
      red: 'bg-red-900/30 text-red-400',
      slate: 'bg-slate-700 text-slate-300',
      violet: 'bg-violet-900/30 text-violet-400',
    };
    return (
      <button
        onClick={() => { onClick(); setMobileSheetOpen(false); }}
        disabled={isProcessing}
        className="flex flex-col items-center gap-1.5 p-3 rounded-xl bg-slate-800 border border-slate-700 hover:border-slate-600 active:scale-95 transition-all disabled:opacity-50"
      >
        <div className={`p-2 rounded-lg ${colorClasses[color]}`}>
          <Icon size={20} />
        </div>
        <span className="text-[11px] text-slate-300 font-medium text-center leading-tight">{label}</span>
      </button>
    );
  };

  return (
    <div className="flex flex-col h-full bg-black">
      <style>{`
        @keyframes scan {
            0% { top: 0%; opacity: 0; }
            10% { opacity: 1; }
            90% { opacity: 1; }
            100% { top: 100%; opacity: 0; }
        }
        .animate-scan {
            animation: scan 2s linear infinite;
        }
      `}</style>
      
      {/* 360 Panorama Mode Banner — pointer-events-none on the wrapper so the
          canvas/sidebar behind stay clickable; only the banner itself captures clicks. */}
      {is360Image && showPanoramaBanner && (
        <div className="absolute top-20 left-1/2 -translate-x-1/2 z-30 flex flex-col items-center gap-3 max-w-md w-[90%] pointer-events-none">
          <div className="relative bg-purple-700/95 backdrop-blur-sm text-white px-5 py-4 pr-10 rounded-xl shadow-xl text-sm border border-purple-400/40 pointer-events-auto">
            <button
              onClick={() => setShowPanoramaBanner(false)}
              className="absolute top-2 right-2 p-1 rounded-md text-white/80 hover:text-white hover:bg-white/10 transition-colors"
              title="Dismiss"
              aria-label="Dismiss panorama banner"
            >
              <X size={16} />
            </button>
            <div className="flex items-start gap-3">
              <span className="text-2xl flex-shrink-0 leading-none">🌐</span>
              <div>
                <div className="font-bold text-base mb-1.5">360° Panorama Mode</div>
                <div className="text-xs opacity-95 leading-relaxed space-y-1">
                  <p>
                    Use the Virtual Staging panel on the right (Stage Room, Stage Outdoor, Match
                    This Image, Clear Furniture). Output keeps the exact 2:1 dimensions and GPano
                    metadata so it stays a valid 360° panorama.
                  </p>
                  <p className="opacity-80">
                    Brush/pin and individual-object tools are hidden — masks don't reproject onto a
                    sphere. Single-shot 360 staging may show floor/ceiling distortion and a back seam.
                  </p>
                </div>
              </div>
            </div>
          </div>
          <button
            onClick={() => setShow360Viewer(true)}
            className="bg-blue-600 hover:bg-blue-500 text-white px-5 py-2.5 rounded-xl shadow-lg font-semibold text-sm flex items-center gap-2 transition-all hover:scale-105 active:scale-95 border border-blue-400/30 pointer-events-auto"
          >
            <Eye size={18} />
            Preview in 360° Viewer
          </button>
        </div>
      )}

      {/* Reopen banner button when dismissed */}
      {is360Image && !showPanoramaBanner && (
        <button
          onClick={() => setShowPanoramaBanner(true)}
          className="absolute top-20 left-1/2 -translate-x-1/2 z-30 px-3 py-1.5 rounded-full text-xs font-medium bg-purple-700/90 hover:bg-purple-600/90 text-white border border-purple-400/40 shadow-md flex items-center gap-1.5 transition-all"
          title="Show 360° Panorama Mode info"
        >
          <span>🌐</span>
          360° Panorama Mode info
        </button>
      )}
      
      {/* Toolbar - Desktop */}
      <div className="hidden md:flex h-16 bg-slate-900 border-b border-slate-800 items-center justify-between px-6 shrink-0 shadow-sm z-10">
        <div className="flex items-center gap-4">
          {!is360Image && (
            <>
              <div className="flex items-center bg-slate-800 rounded-lg p-1 gap-1">
                <button
                  onClick={() => { setMode('brush'); setIsPlacingPin(false); }}
                  className={`p-2 rounded-md transition-all ${mode === 'brush' && !isPlacingPin ? 'bg-slate-700 shadow text-orange-400' : 'text-slate-400 hover:text-slate-200'}`}
                  title="Brush Tool"
                >
                  <Brush size={20} />
                </button>
                <button
                  onClick={() => { setMode('eraser'); setIsPlacingPin(false); }}
                  className={`p-2 rounded-md transition-all ${mode === 'eraser' && !isPlacingPin ? 'bg-slate-700 shadow text-orange-400' : 'text-slate-400 hover:text-slate-200'}`}
                  title="Eraser Tool"
                >
                  <Eraser size={20} />
                </button>
              </div>

              <div className="h-6 w-px bg-slate-700 mx-2"></div>

              <div className="flex items-center gap-3">
                 <span className="text-xs font-medium text-slate-400 uppercase tracking-wider">Size</span>
                 <input 
                   type="range" 
                   min={MIN_BRUSH_SIZE} 
                   max={MAX_BRUSH_SIZE} 
                   value={brushSize} 
                   onChange={(e) => setBrushSize(parseInt(e.target.value))}
                   className="w-32 accent-orange-500 cursor-pointer"
                 />
                 <div 
                   className="rounded-full bg-orange-500/20 border border-orange-500"
                   style={{ width: brushSize / 2, height: brushSize / 2 }}
                 ></div>
              </div>
            </>
          )}
          {is360Image && (
            <div className="text-purple-300 italic text-sm flex items-center gap-2">
              <span className="inline-block w-2 h-2 bg-purple-400 rounded-full animate-pulse"></span>
              360° Panorama Mode — auto-staging only
            </div>
          )}
        </div>

        <div className="flex items-center gap-2">
          {!is360Image && (
            <>
              <button
                onClick={handleUndo}
                disabled={history.length === 0}
                className="p-2 text-slate-400 hover:text-white disabled:opacity-30 transition-colors"
                title="Undo"
              >
                <Undo size={20} />
              </button>
              <button
                onClick={handleRedo}
                disabled={redoHistory.length === 0}
                className="p-2 text-slate-400 hover:text-white disabled:opacity-30 transition-colors"
                title="Redo"
              >
                <Redo size={20} />
              </button>
              <button
                onClick={handleClear}
                disabled={!hasMask && !pinLocation}
                className="p-2 text-slate-400 hover:text-red-400 disabled:opacity-30 transition-colors"
                title="Clear Mask"
              >
                <Trash2 size={20} />
              </button>

              <div className="w-px h-6 bg-slate-800 mx-1"></div>
            </>
          )}
          
          {onForwardToResult && (
            <button
              onClick={onForwardToResult}
              className="flex items-center gap-1.5 pl-2.5 pr-3 py-1.5 rounded-lg bg-purple-600/20 text-purple-200 border border-purple-500/40 hover:bg-purple-600/30 hover:text-white transition-colors font-medium text-sm"
              title="Return to the result you just generated"
            >
              <span>Forward to result</span>
              <ArrowRight size={18} />
            </button>
          )}

          <button
            onClick={onOpenDashboard}
            className="p-2 text-slate-400 hover:text-orange-400 transition-colors"
            title="Dashboard"
          >
            <LayoutDashboard size={20} />
          </button>
          
          <button
            onClick={onDownload}
            className="p-2 text-slate-400 hover:text-orange-400 transition-colors"
            title="Download Image"
          >
            <Download size={20} />
          </button>
          
          {is360Image && (
            <button
              onClick={() => setShow360Viewer(true)}
              className="p-2 text-blue-400 hover:text-blue-300 transition-colors"
              title="View in 360° Viewer"
            >
              <Eye size={20} />
            </button>
          )}
        </div>
      </div>
      
      {/* Toolbar - Mobile */}
      <div className="md:hidden h-14 bg-slate-900 border-b border-slate-800 flex items-center justify-between px-3 shrink-0 shadow-sm z-10">
        <div className="flex items-center gap-2">
          <div className="flex items-center bg-slate-800 rounded-lg p-0.5 gap-0.5">
            <button
              onClick={() => { setMode('brush'); setIsPlacingPin(false); }}
              className={`p-2 rounded-md transition-all ${mode === 'brush' && !isPlacingPin ? 'bg-slate-700 shadow text-orange-400' : 'text-slate-400'}`}
            >
              <Brush size={18} />
            </button>
            <button
              onClick={() => { setMode('eraser'); setIsPlacingPin(false); }}
              className={`p-2 rounded-md transition-all ${mode === 'eraser' && !isPlacingPin ? 'bg-slate-700 shadow text-orange-400' : 'text-slate-400'}`}
            >
              <Eraser size={18} />
            </button>
          </div>
          
          <input 
            type="range" 
            min={MIN_BRUSH_SIZE} 
            max={MAX_BRUSH_SIZE} 
            value={brushSize} 
            onChange={(e) => setBrushSize(parseInt(e.target.value))}
            className="w-20 accent-orange-500"
          />
        </div>

        <div className="flex items-center gap-1">
          <button onClick={handleUndo} disabled={history.length === 0} className="p-2 text-slate-400 disabled:opacity-30">
            <Undo size={18} />
          </button>
          <button onClick={handleClear} disabled={!hasMask && !pinLocation} className="p-2 text-slate-400 disabled:opacity-30">
            <Trash2 size={18} />
          </button>
          {onForwardToResult && (
            <button
              onClick={onForwardToResult}
              className="p-2 text-purple-300 hover:text-white transition-colors"
              title="Return to the result you just generated"
            >
              <ArrowRight size={18} />
            </button>
          )}
          <button onClick={onOpenDashboard} className="p-2 text-slate-400">
            <LayoutDashboard size={18} />
          </button>
          <button onClick={onDownload} className="p-2 text-slate-400">
            <Download size={18} />
          </button>
        </div>
      </div>

      {/* Main Workspace */}
      <div className="flex-1 flex overflow-hidden relative">
        {/* Canvas Area */}
        <div 
          ref={containerRef}
          className="flex-1 bg-black flex items-center justify-center p-4 md:p-8 relative overflow-hidden"
        >
          {/* Background pattern */}
          <div className="absolute inset-0 opacity-5" 
               style={{backgroundImage: 'radial-gradient(#475569 1px, transparent 1px)', backgroundSize: '20px 20px'}}></div>

          {canvasSize.width > 0 && (
            <div 
              style={{ width: canvasSize.width, height: canvasSize.height }}
              className={`relative shadow-2xl shadow-black rounded-sm overflow-hidden bg-slate-900 select-none ring-1 ring-white/5 ${isPlacingPin ? 'cursor-crosshair' : 'cursor-crosshair'}`}
            >
              <img 
                src={imageUrl} 
                className="absolute inset-0 w-full h-full object-contain pointer-events-none select-none"
                alt="Original"
              />
              
              {/* Pin Marker */}
              {pinLocation && (
                  <div 
                    className="absolute z-30 pointer-events-none"
                    style={{ left: pinLocation.x * canvasSize.width, top: pinLocation.y * canvasSize.height }}
                  >
                      <div className="relative -top-3 -left-3 flex flex-col items-center">
                         <div className="w-6 h-6 rounded-full border-2 border-white bg-pink-500 shadow-lg flex items-center justify-center animate-in zoom-in duration-200">
                             <div className="w-1.5 h-1.5 bg-white rounded-full"></div>
                         </div>
                         <div className="w-0.5 h-4 bg-white/50 absolute top-5 shadow-[0_0_2px_black]"></div>
                      </div>
                  </div>
              )}

              {!is360Image && (
                <canvas
                  ref={canvasRef}
                  onMouseDown={handleCanvasMouseDown}
                  onMouseMove={draw}
                  onMouseUp={stopDrawing}
                  onMouseLeave={stopDrawing}
                  onTouchStart={handleCanvasMouseDown}
                  onTouchMove={draw}
                  onTouchEnd={stopDrawing}
                  className={`absolute inset-0 z-10 touch-none ${isPlacingPin ? 'cursor-crosshair' : ''}`}
                />
              )}
              
              {/* Processing Overlay */}
              {isProcessing && (
                <div className="absolute inset-0 z-50 flex flex-col items-center justify-center bg-black/60 backdrop-blur-[2px] animate-in fade-in duration-300">
                    <div className="absolute inset-0 overflow-hidden pointer-events-none">
                        <div className="w-full h-1 bg-gradient-to-r from-transparent via-purple-400 to-transparent absolute top-0 animate-scan shadow-[0_0_15px_rgba(168,85,247,0.5)]"></div>
                    </div>
                    <div className="bg-slate-900/90 p-4 pr-6 rounded-2xl shadow-2xl border border-slate-700 flex items-center gap-4 backdrop-blur-md">
                        <Loader2 className="w-6 h-6 text-purple-500 animate-spin" />
                        <div>
                            <p className="font-semibold text-slate-100">
                              {currentAction === 'clear' && 'Clearing Room...'}
                              {currentAction === 'stage' && 'Staging Room...'}
                              {currentAction === 'renovate' && 'Renovating Room...'}
                              {currentAction === 'brighten' && 'Enhancing Image...'}
                              {currentAction === 'sky' && 'Replacing Sky...'}
                              {currentAction === 'remove' && 'Removing Selection...'}
                              {currentAction === 'custom' && 'Processing Request...'}
                              {!['clear', 'stage', 'renovate', 'brighten', 'sky', 'remove', 'custom'].includes(currentAction) && 'Processing Image...'}
                            </p>
                            <p className="text-xs text-slate-400">
                              {currentAction === 'clear' && 'Removing existing furniture from your room'}
                              {currentAction === 'stage' && 'Adding virtual furniture and decor'}
                              {currentAction === 'renovate' && 'Finishing the build, then staging the room'}
                              {currentAction === 'brighten' && 'Improving lighting and colors'}
                              {currentAction === 'sky' && 'Creating a beautiful new sky'}
                              {currentAction === 'remove' && 'Deleting the selected area'}
                              {currentAction === 'custom' && 'Applying your custom edit'}
                              {!['clear', 'stage', 'renovate', 'brighten', 'sky', 'remove', 'custom'].includes(currentAction) && 'AI is working on your image'}
                            </p>
                        </div>
                    </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Sidebar Controls - Desktop Only */}
        <div className="hidden md:flex w-80 bg-slate-900 border-l border-slate-800 flex-col shrink-0 shadow-xl z-20 overflow-y-auto">
            {showModelPicker && (
              <div className="p-4 border-b border-amber-800/40 bg-amber-950/30">
                <p className="text-[10px] font-semibold uppercase tracking-wider text-amber-300/90 mb-2">
                  Image model (admin / dev)
                </p>
                <div className="grid grid-cols-3 gap-1.5">
                  {STAGING_IMAGE_MODELS.map((m) => (
                    <button
                      key={m.id}
                      type="button"
                      disabled={isProcessing}
                      title={m.description}
                      onClick={() => {
                        setTrialModel(m.id);
                        setStagingImageModel(m.id);
                      }}
                      className={`px-2 py-2 rounded-lg text-[11px] font-semibold border transition-all ${
                        trialModel === m.id
                          ? 'bg-amber-500 text-slate-900 border-amber-400'
                          : 'bg-slate-900 text-amber-100/80 border-slate-700 hover:border-amber-600/50'
                      }`}
                    >
                      {m.shortLabel}
                    </button>
                  ))}
                </div>
                <p className="text-[10px] text-amber-200/60 mt-2 leading-snug">
                  {STAGING_IMAGE_MODELS.find((m) => m.id === trialModel)?.label}
                </p>
              </div>
            )}
            {/* Keep the free-text edit box visible at the top of the controls. */}
            {!is360Image && (
              <div className="p-6 border-b border-slate-800 bg-gradient-to-b from-orange-900/10 to-transparent">
                <h3 className="font-semibold text-slate-200 mb-2 text-sm uppercase tracking-wider flex items-center gap-2">
                  <Wand2 size={16} className="text-orange-400" />
                  Custom Request
                </h3>
                <p className="text-sm text-slate-400 mb-3">
                  Paint over items to modify them, or leave blank to edit the entire image.
                  Anything you type here is also applied to Stage / Renovation / Outdoor below.
                </p>
                <textarea
                  className="w-full h-24 text-sm p-3 border border-slate-700 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none resize-none bg-slate-800 text-slate-200 placeholder-slate-500"
                  value={customPrompt}
                  onChange={(e) => setCustomPrompt(e.target.value)}
                  placeholder="Describe your request (e.g. 'remove the sofa', 'make the sky blue')..."
                />
                <button
                  onClick={handleManualProcess}
                  disabled={isProcessing || (!hasMask && !customPrompt.trim())}
                  className={`
                    w-full mt-3 py-3 rounded-xl font-semibold text-white flex items-center justify-center gap-2 transition-all shadow-lg
                    ${isProcessing || (!hasMask && !customPrompt.trim())
                      ? 'bg-slate-700 cursor-not-allowed shadow-none text-slate-500'
                      : 'bg-orange-600 hover:bg-orange-500 hover:shadow-orange-500/25 active:scale-[0.98]'
                    }
                  `}
                >
                  {isProcessing ? (
                    <>
                      <Loader2 size={20} className="animate-spin" />
                      Processing...
                    </>
                  ) : hasMask && !customPrompt.trim() ? (
                    <>Remove Selected <ArrowRight size={20} /></>
                  ) : (
                    <>Apply Custom Edit <Wand2 size={18} /></>
                  )}
                </button>
              </div>
            )}

            {/* Virtual Staging Section */}
            <div className="p-6 border-b border-slate-800 bg-gradient-to-b from-purple-900/10 to-transparent">
              <h3 className="font-semibold text-purple-300 mb-4 flex items-center gap-2">
                <Sofa size={16} className="text-purple-400"/>
                Virtual Staging
              </h3>

              <div className="mb-4 rounded-xl border border-purple-500/30 bg-purple-900/20 px-3 py-2.5">
                <p className="text-[11px] uppercase tracking-wide text-purple-300/80 font-semibold">Staging Bundle</p>
                <p className="text-sm font-semibold text-purple-100">
                  {bundleGenerationsRemaining} of {bundleSize} generations remaining
                </p>
                <p className="text-[11px] text-purple-200/70 mt-0.5">
                  {bundleGenerationsRemaining > 0
                    ? `This photo has up to ${bundleSize} generations in the current bundle.`
                    : `Generate — ${bundleCost} credits.`}
                </p>
              </div>
              
              <div className="space-y-3">
                {/* Additional Context — free-text guidance layered on top of the
                    staging prompt (Stage This Room / Match This Image). */}
                <div className="space-y-2">
                  <label
                    htmlFor="staging-extra-instructions"
                    className="text-xs font-medium text-slate-400 flex items-center gap-1.5"
                  >
                    <Sparkles size={12} className="text-purple-400" />
                    Additional Context <span className="text-[10px] font-normal text-slate-500">(optional)</span>
                  </label>
                  <textarea
                    id="staging-extra-instructions"
                    value={extraInstructions}
                    onChange={(e) => setExtraInstructions(e.target.value)}
                    disabled={isProcessing}
                    placeholder="e.g. No curtains, prefer light wood, leave space on each side of the bed for nightstands"
                    rows={3}
                    maxLength={600}
                    className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-xs text-white placeholder-slate-500 focus:outline-none focus:border-purple-500 resize-none disabled:opacity-50"
                  />
                  <p className="text-[10px] text-slate-500 leading-snug">
                    Extra guidance for the AI, applied on top of the structural rules — it can steer the
                    style and arrangement, but cannot override room preservation.
                  </p>
                </div>

                {/* Clear All Furniture - Primary Action */}
                <button
                  onClick={() => handleAutoProcess(CLEAR_FURNITURE_PROMPT, 'clear')}
                  disabled={isProcessing}
                  className={`
                    w-full py-3 px-4 rounded-xl font-medium text-sm text-left flex items-center gap-3 transition-all border-2
                    ${isProcessing
                      ? 'bg-slate-800 border-slate-700 text-slate-500 cursor-not-allowed' 
                      : 'bg-gradient-to-r from-red-900/30 to-orange-900/30 border-red-600/60 text-red-200 hover:from-red-800/40 hover:to-orange-800/40 hover:border-red-500 hover:shadow-lg hover:shadow-red-900/20 active:scale-[0.98]'
                    }
                  `}
                >
                    <div className="p-1.5 bg-red-600/40 rounded-lg text-red-300 shrink-0">
                        <Trash2 size={16} />
                    </div>
                    <div className="flex flex-col">
                        <span className="font-bold">{isProcessing ? 'Processing...' : 'Clear All Furniture'}</span>
                        <span className="text-[10px] text-red-300/70 leading-none mt-0.5">Remove everything to empty room</span>
                    </div>
                </button>

                {/* Room Type Selector */}
                <div className="space-y-2">
                  <label className="text-xs font-medium text-slate-400">Room Type</label>
                  <select
                    value={selectedRoomType}
                    onChange={(e) => {
                      const value = e.target.value;
                      setSelectedRoomType(value);
                      onSelectedRoomTypeChange(value);
                    }}
                    className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-purple-500"
                  >
                    {ROOM_TYPES.map(room => (
                      <option key={room.id} value={room.id}>{room.label}</option>
                    ))}
                  </select>
                </div>

                {/* Furniture Style Selector */}
                <div className="space-y-2">
                  <label className="text-xs font-medium text-slate-400">Furniture Style</label>
                  <select
                    value={selectedStyle}
                    onChange={(e) => {
                      const value = e.target.value;
                      setSelectedStyle(value);
                      onSelectedStyleChange(value);
                    }}
                    className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-purple-500"
                  >
                    {FURNITURE_STYLES.map(style => (
                      <option key={style.id} value={style.id}>{style.label}</option>
                    ))}
                  </select>
                </div>

                {/* Property Tier Selector */}
                <div className="space-y-2">
                  <label className="text-xs font-medium text-slate-400">Property Tier</label>
                  <select
                    value={selectedStagingTier}
                    onChange={(e) => {
                      const value = e.target.value;
                      setSelectedStagingTier(value);
                      onSelectedStagingTierChange(value);
                    }}
                    className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-amber-500/70"
                  >
                    {STAGING_TIERS.map(tier => (
                      <option key={tier.id} value={tier.id}>{tier.label}</option>
                    ))}
                  </select>
                  <p className="text-[10px] text-slate-500 leading-snug">
                    {STAGING_TIERS.find(t => t.id === selectedStagingTier)?.description}
                  </p>
                </div>

                {/* Upload reference image - when no reference yet */}
                {!referenceImageUrl && onSetReferenceImage && (
                  <label className="block mb-3">
                    <span className="text-xs text-slate-400 mb-1.5 block">Match an existing style</span>
                    <input
                      type="file"
                      accept={ACCEPTED_IMAGE_TYPES.join(',')}
                      className="sr-only"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) {
                          const reader = new FileReader();
                          reader.onloadend = () => reader.result && onSetReferenceImage(reader.result as string);
                          reader.readAsDataURL(file);
                        }
                        e.target.value = '';
                      }}
                    />
                    <span className="inline-flex items-center gap-2 py-2 px-3 rounded-lg border border-slate-600 text-slate-400 hover:border-purple-500/50 hover:text-purple-200 text-xs font-medium cursor-pointer transition-colors">
                      <Copy size={14} />
                      Upload image to match
                    </span>
                  </label>
                )}
                {/* Match this image - when reference is set */}
                {referenceImageUrl && (
                  <div className="flex items-center gap-2 mb-3 p-2 rounded-xl bg-purple-900/20 border border-purple-500/40">
                    <div className="w-10 h-10 rounded-lg overflow-hidden border border-purple-500/50 shrink-0 bg-slate-900">
                      <img src={referenceImageUrl} alt={sameRoomMode ? 'Staged hero angle' : 'Reference'} className="w-full h-full object-cover" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-medium text-purple-200">{sameRoomMode ? 'Matching this room' : 'Match this style'}</p>
                      <p className="text-[10px] text-slate-400 truncate">{sameRoomMode ? 'Same furniture, this angle' : 'Copy furniture look only'}</p>
                    </div>
                    {onClearReference && (
                      <button type="button" onClick={onClearReference} className="p-1.5 text-slate-400 hover:text-white rounded-lg hover:bg-slate-800 shrink-0" aria-label="Clear reference"><X size={14} /></button>
                    )}
                  </div>
                )}
                {referenceImageUrl && (
                  <button
                    onClick={handleMatchStyleProcess}
                    disabled={isProcessing}
                    className={`
                      w-full py-3 px-4 rounded-xl font-medium text-sm text-left flex items-center gap-3 transition-all border-2 mb-3
                      ${isProcessing
                        ? 'bg-slate-800 border-slate-700 text-slate-500 cursor-not-allowed'
                        : 'bg-gradient-to-r from-purple-600 to-violet-600 border-purple-500 text-white hover:from-purple-500 hover:to-violet-500 hover:shadow-lg hover:shadow-purple-900/30 active:scale-[0.98]'
                      }
                    `}
                  >
                    <div className="p-1.5 bg-white/20 rounded-lg shrink-0">
                      <Copy size={16} />
                    </div>
                    <div className="flex flex-col">
                        <span className="font-bold">{isProcessing ? 'Staging...' : sameRoomMode ? `Stage This Angle — ${bundleCost} credits` : `Match This Style — ${bundleCost} credits`}</span>
                      <span className="text-[10px] text-purple-200/70 leading-none mt-0.5">{sameRoomMode ? 'Reuse the same furniture & layout' : 'Copy furniture style only'}</span>
                    </div>
                  </button>
                )}
                {/* Stage Room — hidden in same-room follow-up mode so users cannot
                    invent a second layout; Stage This Angle is the only path. */}
                {!sameRoomMode && (
                <button
                  onClick={() => {
                    const roomType = ROOM_TYPES.find(r => r.id === selectedRoomType);
                    const prompt = STAGE_ROOM_PROMPT
                      .replace('{{ROOM_TYPE}}', roomType?.description || 'living room')
                      .replace('{{STYLE}}', buildStagingStyleDescription(selectedStyle, selectedStagingTier));
                    handleAutoProcess(prompt, 'stage');
                  }}
                  disabled={isProcessing}
                  className={`
                    w-full py-3 px-4 rounded-xl font-medium text-sm text-left flex items-center gap-3 transition-all border-2
                    ${isProcessing
                      ? 'bg-slate-800 border-slate-700 text-slate-500 cursor-not-allowed' 
                      : 'bg-gradient-to-r from-purple-600 to-indigo-600 border-purple-500 text-white hover:from-purple-500 hover:to-indigo-500 hover:shadow-lg hover:shadow-purple-900/30 active:scale-[0.98]'
                    }
                  `}
                >
                    <div className="p-1.5 bg-white/20 rounded-lg shrink-0">
                        <Armchair size={16} />
                    </div>
                    <div className="flex flex-col">
                        <span className="font-bold">{isProcessing ? 'Staging...' : `Stage This Room — ${bundleCost} credits`}</span>
                        <span className="text-[10px] text-purple-200/70 leading-none mt-0.5">Add furniture & decor</span>
                    </div>
                </button>
                )}

                {!sameRoomMode && (
                <button
                  onClick={handleFullRenovation}
                  disabled={isProcessing}
                  className={`
                    w-full py-3 px-4 rounded-xl font-medium text-sm text-left flex items-center gap-3 transition-all border-2
                    ${isProcessing
                      ? 'bg-slate-800 border-slate-700 text-slate-500 cursor-not-allowed'
                      : 'bg-gradient-to-r from-amber-700 to-orange-600 border-amber-500 text-white hover:from-amber-600 hover:to-orange-500 hover:shadow-lg hover:shadow-amber-900/30 active:scale-[0.98]'
                    }
                  `}
                >
                    <div className="p-1.5 bg-white/20 rounded-lg shrink-0">
                        <Hammer size={16} />
                    </div>
                    <div className="flex flex-col">
                        <span className="font-bold">{isProcessing ? 'Renovating...' : `Full Renovation — ${STAGING_FULL_RENOVATION_CREDITS} credits`}</span>
                        <span className="text-[10px] text-amber-100/80 leading-none mt-0.5">Finish construction, then stage</span>
                    </div>
                </button>
                )}

                {/* Stage Outdoor — surfaced inside Virtual Staging when in 360° mode.
                    Hidden in same-room follow-up mode (layout must come from the hero). */}
                {is360Image && !sameRoomMode && (
                  <div className="rounded-xl border bg-slate-800 border-slate-700 hover:border-teal-500/50 transition-all overflow-hidden">
                    <button
                      onClick={handleStageOutdoor}
                      disabled={isProcessing}
                      className="w-full py-3 px-4 text-left flex items-center gap-3 hover:bg-slate-700/50 transition-colors"
                    >
                      <div className="p-1.5 bg-teal-900/30 rounded-lg text-teal-400 shrink-0">
                        <Fence size={16} />
                      </div>
                      <div className="flex flex-col items-start flex-1">
                        <span className="font-medium text-sm text-teal-200">Stage Outdoor Space</span>
                        <span className="text-[10px] opacity-70 font-normal text-teal-200/70">Gardens, terraces, patios</span>
                      </div>
                      <span className="shrink-0 px-3 py-1 bg-teal-600/30 border border-teal-500/40 text-teal-300 text-xs font-semibold rounded-full flex items-center gap-1">
                        Go <ArrowRight size={12} />
                      </span>
                    </button>
                    <div className="px-4 pb-3 pt-0 bg-slate-800 border-t border-slate-700/50">
                      <div className="mt-2 space-y-2">
                        <div>
                          <label className="text-[10px] text-slate-500 uppercase font-semibold tracking-wider mb-1 block">Space Type</label>
                          <select
                            value={selectedOutdoorSpace}
                            onChange={(e) => setSelectedOutdoorSpace(e.target.value)}
                            disabled={isProcessing}
                            className="w-full bg-slate-900 text-xs text-slate-300 border border-slate-700 rounded-md py-2 px-2 outline-none focus:border-teal-500/50"
                          >
                            {OUTDOOR_SPACE_TYPES.map(space => (
                              <option key={space.id} value={space.id}>{space.label}</option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label className="text-[10px] text-slate-500 uppercase font-semibold tracking-wider mb-1 block">Style</label>
                          <select
                            value={selectedOutdoorStyle}
                            onChange={(e) => setSelectedOutdoorStyle(e.target.value)}
                            disabled={isProcessing}
                            className="w-full bg-slate-900 text-xs text-slate-300 border border-slate-700 rounded-md py-2 px-2 outline-none focus:border-teal-500/50"
                          >
                            {OUTDOOR_STYLES.map(style => (
                              <option key={style.id} value={style.id}>{style.label}</option>
                            ))}
                          </select>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Remove Tripod — repair the nadir (bottom center) where the camera tripod was visible */}
                {is360Image && (
                  <button
                    onClick={() => handleAutoProcess(REMOVE_TRIPOD_360_PROMPT, 'remove-tripod')}
                    disabled={isProcessing}
                    className={`
                      w-full py-3 px-4 rounded-xl font-medium text-sm text-left flex items-center gap-3 transition-all border
                      ${isProcessing
                        ? 'bg-slate-800 border-slate-700 text-slate-500 cursor-not-allowed'
                        : 'bg-slate-800 border-amber-600/50 text-amber-200 hover:bg-slate-700 hover:border-amber-500/70 hover:shadow-md active:scale-[0.98]'
                      }
                    `}
                  >
                    <div className="p-1.5 bg-amber-900/30 rounded-lg text-amber-400 shrink-0">
                      <Eraser size={16} />
                    </div>
                    <div className="flex flex-col">
                      <span className="font-bold">{isProcessing ? 'Removing...' : 'Remove Tripod'}</span>
                      <span className="text-[10px] text-amber-300/70 leading-none mt-0.5">Repair the nadir / bottom center</span>
                    </div>
                  </button>
                )}

                {/* Open 360° Viewer — quick way to preview the panorama before staging */}
                {is360Image && (
                  <button
                    onClick={() => setShow360Viewer(true)}
                    disabled={isProcessing}
                    className="w-full py-2.5 px-4 rounded-xl bg-slate-800 border border-blue-500/40 text-blue-200 hover:bg-slate-700 hover:border-blue-400 text-sm font-medium flex items-center justify-center gap-2 transition-all disabled:opacity-50"
                  >
                    <Eye size={16} />
                    Preview in 360° Viewer
                  </button>
                )}
              </div>
            </div>

            {/* Additional Enhancements Section — hidden in 360° mode (mask/pin tools don't reproject correctly onto a sphere) */}
            {!is360Image && (
            <div className="p-6 border-b border-slate-800">
              <h3 className="font-semibold text-slate-300 mb-4 flex items-center gap-2">
                <Sparkles size={16} className="text-slate-400"/>
                Enhancements
              </h3>
              
              <div className="space-y-3">
                {/* Remove Selection Button - Only shown when mask is present */}
                {hasMask && (
                  <button
                    onClick={handleRemoveSelection}
                    disabled={isProcessing}
                    className={`
                      w-full py-3 px-4 rounded-xl font-medium text-sm text-left flex items-center gap-3 transition-all border
                      ${isProcessing
                        ? 'bg-slate-800 border-slate-700 text-slate-500 cursor-not-allowed' 
                        : 'bg-slate-800 border-slate-700 text-red-200 hover:bg-slate-700 hover:border-red-500/50 hover:shadow-md active:scale-[0.98]'
                      }
                    `}
                  >
                    <div className="p-1.5 bg-red-900/30 rounded-lg text-red-400 shrink-0">
                        <Eraser size={16} />
                    </div>
                    <div className="flex flex-col">
                        <span>{isProcessing ? 'Processing...' : 'Remove Selection'}</span>
                        <span className="text-[10px] text-red-400/70 leading-none mt-0.5">Delete painted area</span>
                    </div>
                  </button>
                )}

                {hasMask && (
                  <button
                    onClick={handleFixOpenings}
                    disabled={isProcessing}
                    className={`
                      w-full py-3 px-4 rounded-xl font-medium text-sm text-left flex items-center gap-3 transition-all border
                      ${isProcessing
                        ? 'bg-slate-800 border-slate-700 text-slate-500 cursor-not-allowed'
                        : 'bg-slate-800 border-amber-700/50 text-amber-100 hover:bg-slate-700 hover:border-amber-500/50 hover:shadow-md active:scale-[0.98]'
                      }
                    `}
                  >
                    <div className="p-1.5 bg-amber-900/30 rounded-lg text-amber-400 shrink-0">
                        <Hammer size={16} />
                    </div>
                    <div className="flex flex-col">
                        <span>{isProcessing ? 'Fixing...' : 'Fix openings'}</span>
                        <span className="text-[10px] text-amber-400/70 leading-none mt-0.5">Restore door/wall from original</span>
                    </div>
                  </button>
                )}

                <button
                  onClick={() => handleAutoProcess(BRIGHTEN_ENHANCE_PROMPT, 'brighten')}
                  disabled={isProcessing}
                  className={`
                    w-full py-3 px-4 rounded-xl font-medium text-sm text-left flex items-center gap-3 transition-all border
                    ${isProcessing
                      ? 'bg-slate-800 border-slate-700 text-slate-500 cursor-not-allowed' 
                      : 'bg-slate-800 border-slate-700 text-yellow-200 hover:bg-slate-700 hover:border-yellow-500/50 hover:shadow-md active:scale-[0.98]'
                    }
                  `}
                >
                    <div className="p-1.5 bg-yellow-900/30 rounded-lg text-yellow-400 shrink-0">
                        <Sun size={16} />
                    </div>
                    <div className="flex flex-col">
                        <span>{isProcessing ? 'Processing...' : 'Brighten & Enhance'}</span>
                        <span className="text-[10px] text-yellow-400/70 leading-none mt-0.5">Improve lighting & colors</span>
                    </div>
                </button>

                {/* Sky Replacement - Blue Sky */}
                <BlueSkyFanControl />

                {/* Sky Replacement - Sunset */}
                <SunsetFanControl />

                {/* Window Sky Replacement - Interior */}
                <button
                  onClick={() => handleAutoProcess(WINDOW_SKY_PROMPT, 'sky')}
                  disabled={isProcessing}
                  className={`
                    w-full py-3 px-4 rounded-xl font-medium text-sm text-left flex items-center gap-3 transition-all border
                    ${isProcessing
                      ? 'bg-slate-800 border-slate-700 text-slate-500 cursor-not-allowed' 
                      : 'bg-slate-800 border-slate-700 text-cyan-200 hover:bg-slate-700 hover:border-cyan-500/50 hover:shadow-md active:scale-[0.98]'
                    }
                  `}
                >
                    <div className="p-1.5 bg-cyan-900/30 rounded-lg text-cyan-400 shrink-0">
                        <Home size={16} />
                    </div>
                    <div className="flex flex-col">
                        <span>{isProcessing ? 'Processing...' : 'Window Sky'}</span>
                        <span className="text-[10px] text-cyan-400/70 leading-none mt-0.5">Sky only — keeps buildings and views</span>
                    </div>
                </button>

                {/* Add Flowers with Options */}
                <div
                  data-botanical-picker
                  className={`
                    rounded-xl border transition-all overflow-hidden
                    ${isProcessing 
                      ? 'bg-slate-800 border-slate-700 opacity-50 cursor-not-allowed' 
                      : flowerPickerOpen
                      ? 'bg-slate-800 border-pink-500/60 shadow-md shadow-pink-900/20'
                      : 'bg-slate-800 border-slate-700 hover:border-pink-500/50 hover:shadow-md'
                    }
                `}>
                    <button
                      onClick={() => {
                        if (isProcessing) return;
                        if (!flowerPickerOpen) {
                          setPlantPickerOpen(false);
                          setSunsetFanOpen(false);
                          setBlueSkyFanOpen(false);
                          setFlowerPickerOpen(true);
                          return;
                        }
                        setFlowerPickerOpen(false);
                      }}
                      disabled={isProcessing}
                      className="w-full py-3 px-4 text-left flex items-center gap-3 hover:bg-slate-700/50 transition-colors"
                    >
                        <div className="p-1.5 bg-pink-900/30 rounded-lg text-pink-400 shrink-0">
                            {pinLocation ? <MapPin size={16} /> : (hasMask ? <Crosshair size={16} /> : <Flower2 size={16} />)}
                        </div>
                        <div className="flex flex-col items-start flex-1">
                            <span className="font-medium text-sm text-pink-200">
                                {pinLocation ? 'Place Flowers Here' : 'Add Flowers'}
                            </span>
                            <span className="text-[10px] opacity-70 font-normal text-pink-200/70">
                                {flowerPickerOpen
                                  ? 'Choose a type, then tap Go'
                                  : pinLocation ? 'At set location' : (hasMask ? 'At masked spot' : 'Auto-place on table')}
                            </span>
                        </div>
                        <ChevronDown
                          size={16}
                          className={`text-pink-400/70 shrink-0 transition-transform duration-300 ${flowerPickerOpen ? 'rotate-180' : ''}`}
                        />
                    </button>
                    
                    <div className={`botanical-panel px-4 pb-3 pt-0 bg-slate-800 border-t border-slate-700/50 ${flowerPickerOpen ? 'botanical-panel-open' : ''}`}>
                        <div className="flex items-start gap-2 mt-2">
                             <div className="flex-1 min-w-0">
                                <div className="flex items-baseline justify-between gap-2 mb-2">
                                  <label className="text-[10px] text-slate-500 uppercase font-semibold tracking-wider">Flower Type</label>
                                  <span className="text-[10px] text-pink-300 truncate">
                                    {FLOWER_TYPES.find((type) => type.id === selectedFlowerId)?.label}
                                  </span>
                                </div>
                                <BotanicalSwatchGrid
                                  types={FLOWER_TYPES}
                                  selectedId={selectedFlowerId}
                                  onSelect={setSelectedFlowerId}
                                  accent="pink"
                                  open={flowerPickerOpen}
                                />
                             </div>
                             
                             <div className="flex flex-col shrink-0 gap-2">
                                <div>
                                  <label className="text-[10px] text-slate-500 uppercase font-semibold tracking-wider mb-1 block">Locate</label>
                                  <button
                                     onClick={(e) => { e.stopPropagation(); setIsPlacingPin(!isPlacingPin); }}
                                     disabled={isProcessing}
                                     className={`p-2 rounded-md border transition-all ${isPlacingPin || pinLocation ? 'bg-pink-900/30 border-pink-500/50 text-pink-400' : 'bg-slate-900 border-slate-700 text-slate-400 hover:bg-slate-800'}`}
                                     title="Set specific location"
                                  >
                                      <MapPin size={16} />
                                  </button>
                                </div>
                                <div>
                                  <label className="text-[10px] text-slate-500 uppercase font-semibold tracking-wider mb-1 block">Go</label>
                                  <button
                                     onClick={(e) => { e.stopPropagation(); handleAddFlowers(); }}
                                     disabled={isProcessing}
                                     className="p-2 rounded-md border bg-pink-600 border-pink-500 text-white hover:bg-pink-500 transition-all disabled:opacity-50"
                                     title="Add flowers"
                                  >
                                      <ArrowRight size={16} />
                                  </button>
                                </div>
                             </div>
                        </div>
                    </div>
                </div>

                {/* Add Plants with Options */}
                <div
                  data-botanical-picker
                  className={`
                    rounded-xl border transition-all overflow-hidden
                    ${isProcessing 
                      ? 'bg-slate-800 border-slate-700 opacity-50 cursor-not-allowed' 
                      : plantPickerOpen
                      ? 'bg-slate-800 border-emerald-500/60 shadow-md shadow-emerald-900/20'
                      : 'bg-slate-800 border-slate-700 hover:border-emerald-500/50 hover:shadow-md'
                    }
                `}>
                    <button
                      onClick={() => {
                        if (isProcessing) return;
                        if (!plantPickerOpen) {
                          setFlowerPickerOpen(false);
                          setSunsetFanOpen(false);
                          setBlueSkyFanOpen(false);
                          setPlantPickerOpen(true);
                          return;
                        }
                        setPlantPickerOpen(false);
                      }}
                      disabled={isProcessing}
                      className="w-full py-3 px-4 text-left flex items-center gap-3 hover:bg-slate-700/50 transition-colors"
                    >
                        <div className="p-1.5 bg-emerald-900/30 rounded-lg text-emerald-400 shrink-0">
                            {pinLocation ? <MapPin size={16} /> : (hasMask ? <Crosshair size={16} /> : <TreeDeciduous size={16} />)}
                        </div>
                        <div className="flex flex-col items-start flex-1">
                            <span className="font-medium text-sm text-emerald-200">
                                {pinLocation ? 'Place Plant Here' : 'Add Plant'}
                            </span>
                            <span className="text-[10px] opacity-70 font-normal text-emerald-200/70">
                                {plantPickerOpen
                                  ? 'Choose a type, then tap Go'
                                  : pinLocation ? 'At set location' : (hasMask ? 'At masked spot' : 'Auto-place in room')}
                            </span>
                        </div>
                        <ChevronDown
                          size={16}
                          className={`text-emerald-400/70 shrink-0 transition-transform duration-300 ${plantPickerOpen ? 'rotate-180' : ''}`}
                        />
                    </button>
                    
                    <div className={`botanical-panel px-4 pb-3 pt-0 bg-slate-800 border-t border-slate-700/50 ${plantPickerOpen ? 'botanical-panel-open' : ''}`}>
                        <div className="flex items-start gap-2 mt-2">
                             <div className="flex-1 min-w-0">
                                <div className="flex items-baseline justify-between gap-2 mb-2">
                                  <label className="text-[10px] text-slate-500 uppercase font-semibold tracking-wider">Plant Type</label>
                                  <span className="text-[10px] text-emerald-300 truncate">
                                    {PLANT_TYPES.find((type) => type.id === selectedPlantId)?.label}
                                  </span>
                                </div>
                                <BotanicalSwatchGrid
                                  types={PLANT_TYPES}
                                  selectedId={selectedPlantId}
                                  onSelect={setSelectedPlantId}
                                  accent="emerald"
                                  open={plantPickerOpen}
                                />
                             </div>
                             
                             <div className="flex flex-col shrink-0 gap-2">
                                <div>
                                  <label className="text-[10px] text-slate-500 uppercase font-semibold tracking-wider mb-1 block">Locate</label>
                                  <button
                                     onClick={(e) => { e.stopPropagation(); setIsPlacingPin(!isPlacingPin); }}
                                     disabled={isProcessing}
                                     className={`p-2 rounded-md border transition-all ${isPlacingPin || pinLocation ? 'bg-emerald-900/30 border-emerald-500/50 text-emerald-400' : 'bg-slate-900 border-slate-700 text-slate-400 hover:bg-slate-800'}`}
                                     title="Set specific location"
                                  >
                                      <MapPin size={16} />
                                  </button>
                                </div>
                                <div>
                                  <label className="text-[10px] text-slate-500 uppercase font-semibold tracking-wider mb-1 block">Go</label>
                                  <button
                                     onClick={(e) => { e.stopPropagation(); handleAddPlants(); }}
                                     disabled={isProcessing}
                                     className="p-2 rounded-md border bg-emerald-600 border-emerald-500 text-white hover:bg-emerald-500 transition-all disabled:opacity-50"
                                     title="Add plant"
                                  >
                                      <ArrowRight size={16} />
                                  </button>
                                </div>
                             </div>
                        </div>
                    </div>
                </div>

                {/* Add Artwork with Options */}
                <div className={`
                    rounded-xl border transition-all overflow-hidden
                    ${isProcessing 
                      ? 'bg-slate-800 border-slate-700 opacity-50 cursor-not-allowed' 
                      : 'bg-slate-800 border-slate-700 hover:border-indigo-500/50 hover:shadow-md'
                    }
                `}>
                    <button
                      onClick={handleAddArtwork}
                      disabled={isProcessing}
                      className="w-full py-3 px-4 text-left flex items-center gap-3 hover:bg-slate-700/50 transition-colors"
                    >
                        <div className="p-1.5 bg-indigo-900/30 rounded-lg text-indigo-400 shrink-0">
                            {pinLocation ? <MapPin size={16} /> : (hasMask ? <Crosshair size={16} /> : <Frame size={16} />)}
                        </div>
                        <div className="flex flex-col items-start flex-1">
                            <span className="font-medium text-sm text-indigo-200">
                                {pinLocation ? 'Place Artwork Here' : 'Add Artwork'}
                            </span>
                            <span className="text-[10px] opacity-70 font-normal text-indigo-200/70">
                                {pinLocation ? 'At set location' : (hasMask ? 'On marked wall area' : 'Auto-place on wall')}
                            </span>
                        </div>
                        <span className="shrink-0 px-3 py-1 bg-indigo-600/30 border border-indigo-500/40 text-indigo-300 text-xs font-semibold rounded-full flex items-center gap-1">
                            Go <ArrowRight size={12} />
                        </span>
                    </button>
                    
                    <div className="px-4 pb-3 pt-0 bg-slate-800 border-t border-slate-700/50">
                        <div className="flex items-center gap-2 mt-2">
                            <div className="flex-1">
                                <label className="text-[10px] text-slate-500 uppercase font-semibold tracking-wider mb-1 block">Artwork Style</label>
                                <select 
                                    value={selectedArtworkId}
                                    onChange={(e) => setSelectedArtworkId(e.target.value)}
                                    disabled={isProcessing}
                                    onClick={(e) => e.stopPropagation()}
                                    className="w-full bg-slate-900 text-xs text-slate-300 border border-slate-700 rounded-md py-2 px-2 outline-none focus:border-indigo-500/50 focus:ring-1 focus:ring-indigo-500/20 cursor-pointer hover:bg-slate-800 transition-colors"
                                >
                                    {ARTWORK_TYPES.map(type => (
                                        <option key={type.id} value={type.id}>{type.label}</option>
                                    ))}
                                </select>
                            </div>
                            
                            <div className="flex flex-col">
                                <label className="text-[10px] text-slate-500 uppercase font-semibold tracking-wider mb-1 block">Locate</label>
                                <button
                                   onClick={(e) => { e.stopPropagation(); setIsPlacingPin(!isPlacingPin); }}
                                   disabled={isProcessing}
                                   className={`p-2 rounded-md border transition-all ${isPlacingPin || pinLocation ? 'bg-indigo-900/30 border-indigo-500/50 text-indigo-400' : 'bg-slate-900 border-slate-700 text-slate-400 hover:bg-slate-800'}`}
                                   title="Set specific location on wall"
                                >
                                    <MapPin size={16} />
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Add Furniture with Options */}
                <div className={`
                    rounded-xl border transition-all overflow-hidden
                    ${isProcessing 
                      ? 'bg-slate-800 border-slate-700 opacity-50 cursor-not-allowed' 
                      : 'bg-slate-800 border-slate-700 hover:border-amber-500/50 hover:shadow-md'
                    }
                `}>
                    <button
                      onClick={handleAddFurniture}
                      disabled={isProcessing}
                      className="w-full py-3 px-4 text-left flex items-center gap-3 hover:bg-slate-700/50 transition-colors"
                    >
                        <div className="p-1.5 bg-amber-900/30 rounded-lg text-amber-400 shrink-0">
                            {pinLocation ? <MapPin size={16} /> : (hasMask ? <Crosshair size={16} /> : <Sofa size={16} />)}
                        </div>
                        <div className="flex flex-col items-start flex-1">
                            <span className="font-medium text-sm text-amber-200">
                                {pinLocation ? 'Place Furniture Here' : 'Add Furniture'}
                            </span>
                            <span className="text-[10px] opacity-70 font-normal text-amber-200/70">
                                {pinLocation ? 'At set location' : (hasMask ? 'In marked area' : 'Auto-place in room')}
                            </span>
                        </div>
                        <span className="shrink-0 px-3 py-1 bg-amber-600/30 border border-amber-500/40 text-amber-300 text-xs font-semibold rounded-full flex items-center gap-1">
                            Go <ArrowRight size={12} />
                        </span>
                    </button>
                    
                    <div className="px-4 pb-3 pt-0 bg-slate-800 border-t border-slate-700/50">
                        <div className="flex items-center gap-2 mt-2">
                            <div className="flex-1">
                                <label className="text-[10px] text-slate-500 uppercase font-semibold tracking-wider mb-1 block">Furniture Item</label>
                                <select 
                                    value={selectedFurnitureId}
                                    onChange={(e) => setSelectedFurnitureId(e.target.value)}
                                    disabled={isProcessing}
                                    onClick={(e) => e.stopPropagation()}
                                    className="w-full bg-slate-900 text-xs text-slate-300 border border-slate-700 rounded-md py-2 px-2 outline-none focus:border-amber-500/50 focus:ring-1 focus:ring-amber-500/20 cursor-pointer hover:bg-slate-800 transition-colors"
                                >
                                    {FURNITURE_ITEMS.map(item => (
                                        <option key={item.id} value={item.id}>{item.label}</option>
                                    ))}
                                </select>
                            </div>
                            
                            <div className="flex flex-col">
                                <label className="text-[10px] text-slate-500 uppercase font-semibold tracking-wider mb-1 block">Locate</label>
                                <button
                                   onClick={(e) => { e.stopPropagation(); setIsPlacingPin(!isPlacingPin); }}
                                   disabled={isProcessing}
                                   className={`p-2 rounded-md border transition-all ${isPlacingPin || pinLocation ? 'bg-amber-900/30 border-amber-500/50 text-amber-400' : 'bg-slate-900 border-slate-700 text-slate-400 hover:bg-slate-800'}`}
                                   title="Set specific location"
                                >
                                    <MapPin size={16} />
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Change Color */}
                <div className={`
                    rounded-xl border transition-all overflow-hidden
                    ${isProcessing 
                      ? 'bg-slate-800 border-slate-700 opacity-50 cursor-not-allowed' 
                      : 'bg-slate-800 border-slate-700 hover:border-violet-500/50 hover:shadow-md'
                    }
                `}>
                    <button
                      onClick={handleChangeColor}
                      disabled={isProcessing || (selectedColorTarget === 'brush-selection' && !hasMask) || (selectedColor === 'custom' && !customColorText.trim())}
                      className="w-full py-3 px-4 text-left flex items-center gap-3 hover:bg-slate-700/50 transition-colors"
                    >
                        <div className="p-1.5 bg-violet-900/30 rounded-lg text-violet-400 shrink-0">
                            <Palette size={16} />
                        </div>
                        <div className="flex flex-col items-start flex-1">
                            <span className="font-medium text-sm text-violet-200">Change Color</span>
                            <span className="text-[10px] opacity-70 font-normal text-violet-200/70">
                                {selectedColorTarget === 'brush-selection' ? (hasMask ? 'Recolor masked area' : 'Paint area first') : `Recolor ${(COLOR_TARGETS.find(t => t.id === selectedColorTarget)?.label ?? 'furniture').toLowerCase()}`}
                            </span>
                        </div>
                        <span className="shrink-0 px-3 py-1 bg-violet-600/30 border border-violet-500/40 text-violet-300 text-xs font-semibold rounded-full flex items-center gap-1">
                            Go <ArrowRight size={12} />
                        </span>
                    </button>
                    
                    <div className="px-4 pb-3 pt-0 bg-slate-800 border-t border-slate-700/50">
                        <div className="mt-2 space-y-2">
                            <div>
                                <label className="text-[10px] text-slate-500 uppercase font-semibold tracking-wider mb-1 block">Target</label>
                                <select 
                                    value={selectedColorTarget}
                                    onChange={(e) => setSelectedColorTarget(e.target.value)}
                                    disabled={isProcessing}
                                    onClick={(e) => e.stopPropagation()}
                                    className="w-full bg-slate-900 text-xs text-slate-300 border border-slate-700 rounded-md py-2 px-2 outline-none focus:border-violet-500/50 focus:ring-1 focus:ring-violet-500/20 cursor-pointer hover:bg-slate-800 transition-colors"
                                >
                                    {COLOR_TARGETS.map(target => (
                                        <option key={target.id} value={target.id}>{target.label}</option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <label className="text-[10px] text-slate-500 uppercase font-semibold tracking-wider mb-1 block">New Color</label>
                                <div className="space-y-1.5">
                                    {Array.from(new Set(COLOR_OPTIONS.map(c => c.group))).map(group => (
                                        <div key={group}>
                                            <span className="text-[9px] text-slate-500 font-medium">{group}</span>
                                            <div className="flex flex-wrap gap-1 mt-0.5">
                                                {COLOR_OPTIONS.filter(c => c.group === group).map(color => (
                                                    <button
                                                        key={color.id}
                                                        onClick={(e) => { e.stopPropagation(); setSelectedColor(color.id); }}
                                                        disabled={isProcessing}
                                                        title={color.label}
                                                        className={`w-5 h-5 rounded-full border-2 transition-all hover:scale-125 ${
                                                            selectedColor === color.id
                                                                ? 'border-violet-400 ring-1 ring-violet-400/50 scale-110'
                                                                : 'border-slate-600 hover:border-slate-400'
                                                        }`}
                                                        style={{ backgroundColor: color.hex }}
                                                    />
                                                ))}
                                            </div>
                                        </div>
                                    ))}
                                    <div>
                                        <button
                                            onClick={(e) => { e.stopPropagation(); setSelectedColor('custom'); }}
                                            disabled={isProcessing}
                                            className={`mt-1 w-full text-left text-[10px] px-2 py-1.5 rounded-md border transition-all ${
                                                selectedColor === 'custom'
                                                    ? 'bg-violet-900/30 border-violet-500/50 text-violet-300'
                                                    : 'bg-slate-900 border-slate-700 text-slate-400 hover:border-slate-500'
                                            }`}
                                        >
                                            Custom colour...
                                        </button>
                                        {selectedColor === 'custom' && (
                                            <input
                                                type="text"
                                                value={customColorText}
                                                onChange={(e) => setCustomColorText(e.target.value)}
                                                onClick={(e) => e.stopPropagation()}
                                                placeholder="e.g. warm terracotta, pale mint"
                                                className="mt-1 w-full bg-slate-900 text-xs text-slate-300 border border-violet-500/50 rounded-md py-1.5 px-2 outline-none focus:ring-1 focus:ring-violet-500/30 placeholder-slate-600"
                                            />
                                        )}
                                    </div>
                                </div>
                                {selectedColor !== 'custom' && (
                                    <p className="text-[10px] text-slate-500 mt-1">
                                        {COLOR_OPTIONS.find(c => c.id === selectedColor)?.label}
                                    </p>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Outside Space */}
                <div className={`
                    rounded-xl border transition-all overflow-hidden
                    ${isProcessing 
                      ? 'bg-slate-800 border-slate-700 opacity-50 cursor-not-allowed' 
                      : 'bg-slate-800 border-slate-700 hover:border-teal-500/50 hover:shadow-md'
                    }
                `}>
                    <button
                      onClick={handleStageOutdoor}
                      disabled={isProcessing}
                      className="w-full py-3 px-4 text-left flex items-center gap-3 hover:bg-slate-700/50 transition-colors"
                    >
                        <div className="p-1.5 bg-teal-900/30 rounded-lg text-teal-400 shrink-0">
                            <Fence size={16} />
                        </div>
                        <div className="flex flex-col items-start flex-1">
                            <span className="font-medium text-sm text-teal-200">Outside Space</span>
                            <span className="text-[10px] opacity-70 font-normal text-teal-200/70">Stage gardens, terraces & more</span>
                        </div>
                        <span className="shrink-0 px-3 py-1 bg-teal-600/30 border border-teal-500/40 text-teal-300 text-xs font-semibold rounded-full flex items-center gap-1">
                            Go <ArrowRight size={12} />
                        </span>
                    </button>
                    
                    <div className="px-4 pb-3 pt-0 bg-slate-800 border-t border-slate-700/50">
                        <div className="mt-2 space-y-2">
                            <div>
                                <label className="text-[10px] text-slate-500 uppercase font-semibold tracking-wider mb-1 block">Space Type</label>
                                <select 
                                    value={selectedOutdoorSpace}
                                    onChange={(e) => setSelectedOutdoorSpace(e.target.value)}
                                    disabled={isProcessing}
                                    onClick={(e) => e.stopPropagation()}
                                    className="w-full bg-slate-900 text-xs text-slate-300 border border-slate-700 rounded-md py-2 px-2 outline-none focus:border-teal-500/50 focus:ring-1 focus:ring-teal-500/20 cursor-pointer hover:bg-slate-800 transition-colors"
                                >
                                    {OUTDOOR_SPACE_TYPES.map(space => (
                                        <option key={space.id} value={space.id}>{space.label}</option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <label className="text-[10px] text-slate-500 uppercase font-semibold tracking-wider mb-1 block">Style</label>
                                <select 
                                    value={selectedOutdoorStyle}
                                    onChange={(e) => setSelectedOutdoorStyle(e.target.value)}
                                    disabled={isProcessing}
                                    onClick={(e) => e.stopPropagation()}
                                    className="w-full bg-slate-900 text-xs text-slate-300 border border-slate-700 rounded-md py-2 px-2 outline-none focus:border-teal-500/50 focus:ring-1 focus:ring-teal-500/20 cursor-pointer hover:bg-slate-800 transition-colors"
                                >
                                    {OUTDOOR_STYLES.map(style => (
                                        <option key={style.id} value={style.id}>{style.label}</option>
                                    ))}
                                </select>
                            </div>
                        </div>
                    </div>
                </div>

              </div>
            </div>
            )}

        </div>
        
        {/* Mobile Bottom Sheet */}
        <div className="md:hidden">
          {/* Collapsed Bar */}
          {!mobileSheetOpen && (
            <div className="fixed bottom-0 left-0 right-0 z-30">
              <button
                onClick={() => setMobileSheetOpen(true)}
                className="w-full bg-gradient-to-t from-slate-900 via-slate-900 to-slate-900/95 border-t border-slate-700 p-4 flex items-center justify-center gap-2 text-orange-400 font-medium"
              >
                <Sparkles size={18} />
                <span>AI Enhancements</span>
                <ChevronUp size={18} />
              </button>
            </div>
          )}
          
          {/* Expanded Sheet */}
          {mobileSheetOpen && (
            <div className="fixed inset-0 z-40 flex flex-col">
              {/* Backdrop */}
              <div 
                className="flex-1 bg-black/60 backdrop-blur-sm"
                onClick={() => setMobileSheetOpen(false)}
              />
              
              {/* Sheet Content */}
              <div className="bg-slate-900 border-t border-slate-700 rounded-t-3xl max-h-[75vh] flex flex-col animate-in slide-in-from-bottom duration-300">
                {/* Handle */}
                <div className="flex justify-center pt-3 pb-2">
                  <div className="w-10 h-1 bg-slate-600 rounded-full" />
                </div>
                
                {/* Header */}
                <div className="flex items-center justify-between px-4 pb-3 border-b border-slate-800">
                  <div className="flex gap-2">
                    <button
                      onClick={() => setMobileTab('quick')}
                      className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${mobileTab === 'quick' ? 'bg-orange-600 text-white' : 'text-slate-400'}`}
                    >
                      Quick Actions
                    </button>
                    <button
                      onClick={() => setMobileTab('custom')}
                      className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${mobileTab === 'custom' ? 'bg-orange-600 text-white' : 'text-slate-400'}`}
                    >
                      Custom
                    </button>
                  </div>
                  <button
                    onClick={() => setMobileSheetOpen(false)}
                    className="p-2 text-slate-400"
                  >
                    <X size={20} />
                  </button>
                </div>
                
                {/* Content */}
                <div className="flex-1 overflow-y-auto p-4">
                  <div className="mb-4 rounded-xl border border-purple-500/30 bg-purple-900/20 px-3 py-2.5">
                    <p className="text-[11px] uppercase tracking-wide text-purple-300/80 font-semibold">Staging Bundle</p>
                    <p className="text-sm font-semibold text-purple-100">
                      {bundleGenerationsRemaining} of {bundleSize} generations remaining
                    </p>
                    <p className="text-[11px] text-purple-200/70 mt-0.5">
                      {bundleGenerationsRemaining > 0
                        ? `This photo has up to ${bundleSize} generations in the current bundle.`
                        : `Generate — ${bundleCost} credits.`}
                    </p>
                  </div>
                  {showModelPicker && (
                    <div className="mb-4 rounded-xl border border-amber-700/40 bg-amber-950/40 px-3 py-2.5">
                      <p className="text-[11px] uppercase tracking-wide text-amber-300/80 font-semibold mb-2">
                        Image model
                      </p>
                      <div className="grid grid-cols-3 gap-1.5">
                        {STAGING_IMAGE_MODELS.map((m) => (
                          <button
                            key={m.id}
                            type="button"
                            disabled={isProcessing}
                            onClick={() => {
                              setTrialModel(m.id);
                              setStagingImageModel(m.id);
                            }}
                            className={`px-2 py-2 rounded-lg text-[11px] font-semibold border ${
                              trialModel === m.id
                                ? 'bg-amber-500 text-slate-900 border-amber-400'
                                : 'bg-slate-900 text-amber-100/80 border-slate-700'
                            }`}
                          >
                            {m.shortLabel}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                  {mobileTab === 'quick' ? (
                    <>
                      {/* Virtual Staging Controls */}
                      <div className="mb-4 space-y-3">
                        {/* Additional Context — extra guidance for the AI */}
                        <div className="space-y-1.5">
                          <label className="text-[11px] font-medium text-slate-400 flex items-center gap-1.5">
                            <Sparkles size={11} className="text-purple-400" />
                            Additional Context <span className="text-[10px] font-normal text-slate-500">(optional)</span>
                          </label>
                          <textarea
                            value={extraInstructions}
                            onChange={(e) => setExtraInstructions(e.target.value)}
                            disabled={isProcessing}
                            placeholder="e.g. No curtains, prefer light wood, leave space for nightstands"
                            rows={2}
                            maxLength={600}
                            className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-xs text-white placeholder-slate-500 focus:outline-none focus:border-purple-500 resize-none disabled:opacity-50"
                          />
                        </div>

                        {/* Clear All Furniture */}
                        <button
                          onClick={() => { handleAutoProcess(CLEAR_FURNITURE_PROMPT, 'clear'); setMobileSheetOpen(false); }}
                          disabled={isProcessing}
                          className="w-full py-3 px-4 rounded-xl bg-gradient-to-r from-red-900/40 to-red-800/40 border-2 border-red-600/60 text-red-200 font-medium flex items-center justify-center gap-2 disabled:opacity-50"
                        >
                          <Trash2 size={18} />
                          Clear All Furniture
                        </button>

                        {/* Room Type, Style & Tier Selectors */}
                        <div className="space-y-2">
                          <div>
                            <label className="text-xs text-slate-400 mb-1 block">Room Type</label>
                            <select
                              value={selectedRoomType}
                              onChange={(e) => {
                                const value = e.target.value;
                                setSelectedRoomType(value);
                                onSelectedRoomTypeChange(value);
                              }}
                              className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white"
                            >
                              {ROOM_TYPES.map(room => (
                                <option key={room.id} value={room.id}>{room.label}</option>
                              ))}
                            </select>
                          </div>
                          <div className="grid grid-cols-2 gap-2">
                            <div>
                              <label className="text-xs text-slate-400 mb-1 block">Style</label>
                              <select
                                value={selectedStyle}
                                onChange={(e) => {
                                  const value = e.target.value;
                                  setSelectedStyle(value);
                                  onSelectedStyleChange(value);
                                }}
                                className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white"
                              >
                                {FURNITURE_STYLES.map(style => (
                                  <option key={style.id} value={style.id}>{style.label}</option>
                                ))}
                              </select>
                            </div>
                            <div>
                              <label className="text-xs text-slate-400 mb-1 block">Property Tier</label>
                              <select
                                value={selectedStagingTier}
                                onChange={(e) => {
                                  const value = e.target.value;
                                  setSelectedStagingTier(value);
                                  onSelectedStagingTierChange(value);
                                }}
                                className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white"
                              >
                                {STAGING_TIERS.map(tier => (
                                  <option key={tier.id} value={tier.id}>{tier.label}</option>
                                ))}
                              </select>
                            </div>
                          </div>
                        </div>

                        {/* Match this image - when reference is set */}
                        {referenceImageUrl && (
                          <>
                            <div className="flex items-center gap-2 mb-2 p-2 rounded-xl bg-purple-900/20 border border-purple-500/40">
                              <div className="w-9 h-9 rounded-lg overflow-hidden border border-purple-500/50 shrink-0 bg-slate-900">
                                <img src={referenceImageUrl} alt={sameRoomMode ? 'Staged hero angle' : 'Reference'} className="w-full h-full object-cover" />
                              </div>
                              <span className="text-xs font-medium text-purple-200 flex-1">{sameRoomMode ? 'Matching this room' : 'Match this image'}</span>
                              {onClearReference && (
                                <button type="button" onClick={() => { onClearReference(); setMobileSheetOpen(false); }} className="p-1.5 text-slate-400 hover:text-white rounded-lg" aria-label="Clear reference"><X size={14} /></button>
                              )}
                            </div>
                            <button
                              onClick={() => { handleMatchStyleProcess(); setMobileSheetOpen(false); }}
                              disabled={isProcessing}
                              className="w-full py-3 px-4 rounded-xl bg-gradient-to-r from-purple-600 to-violet-600 border-2 border-purple-500 text-white font-medium flex items-center justify-center gap-2 disabled:opacity-50 mb-3"
                            >
                              <Copy size={18} />
                              {sameRoomMode ? `Stage This Angle — ${bundleCost} credits` : `Match This Style — ${bundleCost} credits`}
                            </button>
                          </>
                        )}
                        {/* Stage This Room — hidden when matching a same-room hero layout */}
                        {!sameRoomMode && (
                        <button
                          onClick={() => {
                            const roomType = ROOM_TYPES.find(r => r.id === selectedRoomType);
                            const prompt = STAGE_ROOM_PROMPT
                              .replace('{{ROOM_TYPE}}', roomType?.description || 'living room')
                              .replace('{{STYLE}}', buildStagingStyleDescription(selectedStyle, selectedStagingTier));
                            handleAutoProcess(prompt, 'stage');
                            setMobileSheetOpen(false);
                          }}
                          disabled={isProcessing}
                          className="w-full py-3 px-4 rounded-xl bg-gradient-to-r from-purple-600 to-indigo-600 border-2 border-purple-500 text-white font-medium flex items-center justify-center gap-2 disabled:opacity-50"
                        >
                          <Armchair size={18} />
                          {`Stage This Room — ${bundleCost} credits`}
                        </button>
                        )}
                        {!sameRoomMode && (
                        <button
                          onClick={() => { handleFullRenovation(); setMobileSheetOpen(false); }}
                          disabled={isProcessing}
                          className="w-full py-3 px-4 rounded-xl bg-gradient-to-r from-amber-700 to-orange-600 border-2 border-amber-500 text-white font-medium flex items-center justify-center gap-2 disabled:opacity-50"
                        >
                          <Hammer size={18} />
                          {`Full Renovation — ${STAGING_FULL_RENOVATION_CREDITS} credits`}
                        </button>
                        )}
                      </div>

                      {/* Stage Outdoor — surfaced for 360° mode; hidden in same-room follow-up */}
                      {is360Image && !sameRoomMode && (
                        <div className="mb-4">
                          <button
                            onClick={() => { handleStageOutdoor(); setMobileSheetOpen(false); }}
                            disabled={isProcessing}
                            className="w-full py-3 px-4 rounded-xl bg-slate-800 border border-teal-500/50 text-teal-200 font-medium flex items-center justify-center gap-2 disabled:opacity-50"
                          >
                            <Fence size={18} />
                            Stage Outdoor Space
                          </button>
                          <button
                            onClick={() => { handleAutoProcess(REMOVE_TRIPOD_360_PROMPT, 'remove-tripod'); setMobileSheetOpen(false); }}
                            disabled={isProcessing}
                            className="mt-2 w-full py-3 px-4 rounded-xl bg-slate-800 border border-amber-500/50 text-amber-200 font-medium flex items-center justify-center gap-2 disabled:opacity-50"
                          >
                            <Eraser size={18} />
                            Remove Tripod
                          </button>
                          <button
                            onClick={() => { setShow360Viewer(true); setMobileSheetOpen(false); }}
                            disabled={isProcessing}
                            className="mt-2 w-full py-3 px-4 rounded-xl bg-slate-800 border border-blue-500/40 text-blue-200 font-medium flex items-center justify-center gap-2 disabled:opacity-50"
                          >
                            <Eye size={18} />
                            Preview in 360° Viewer
                          </button>
                        </div>
                      )}

                      {/* Remove Selection - show only when mask exists */}
                      {hasMask && !is360Image && (
                        <div className="mb-4 space-y-2">
                          <button
                            onClick={() => { handleRemoveSelection(); setMobileSheetOpen(false); }}
                            disabled={isProcessing}
                            className="w-full py-3 px-4 rounded-xl bg-slate-800 border border-red-600/50 text-red-200 font-medium flex items-center justify-center gap-2 disabled:opacity-50"
                          >
                            <Eraser size={18} />
                            Remove Selection
                          </button>
                          <button
                            onClick={() => { handleFixOpenings(); setMobileSheetOpen(false); }}
                            disabled={isProcessing}
                            className="w-full py-3 px-4 rounded-xl bg-slate-800 border border-amber-600/50 text-amber-100 font-medium flex items-center justify-center gap-2 disabled:opacity-50"
                          >
                            <Hammer size={18} />
                            Fix openings
                          </button>
                        </div>
                      )}

                      {/* Enhancements grids — hidden in 360° mode */}
                      {!is360Image && (
                        <>
                          <h4 className="text-xs font-medium text-slate-400 mb-2">Enhancements</h4>
                          <div className="grid grid-cols-4 gap-2 mb-4">
                            <MobileActionButton onClick={() => handleAutoProcess(BRIGHTEN_ENHANCE_PROMPT, 'brighten')} icon={Sun} label="Brighten" color="yellow" />
                            <MobileActionButton onClick={handleAddArtwork} icon={Frame} label="Artwork" color="slate" />
                          </div>
                          <div className="mb-4 space-y-3">
                            <BlueSkyFanControl compact />
                            <SunsetFanControl compact />
                          </div>

                          <div className="grid grid-cols-4 gap-2 mb-4">
                            <MobileActionButton onClick={() => handleAutoProcess(WINDOW_SKY_PROMPT, 'sky')} icon={Home} label="Window" color="blue" />
                            <MobileActionButton onClick={handleAddFlowers} icon={Flower2} label="Flowers" color="pink" />
                            <MobileActionButton onClick={handleAddPlants} icon={TreeDeciduous} label="Plants" color="emerald" />
                            <MobileActionButton onClick={handleChangeColor} icon={Palette} label="Recolor" color="violet" />
                          </div>
                        </>
                      )}
                      
                      {/* Style Selectors — hidden in 360° mode (rely on mask/pin tools) */}
                      {!is360Image && (
                      <div className="space-y-3">
                        {/* Artwork Style Selector */}
                        <div className="bg-slate-800 rounded-xl p-3 border border-slate-700">
                          <div className="flex items-center gap-2 mb-2">
                            <Frame size={16} className="text-indigo-400" />
                            <span className="text-sm text-slate-300 font-medium">Artwork Style</span>
                          </div>
                          <select 
                            value={selectedArtworkId}
                            onChange={(e) => setSelectedArtworkId(e.target.value)}
                            className="w-full bg-slate-900 text-sm text-slate-300 border border-slate-700 rounded-lg py-2.5 px-3"
                          >
                            {ARTWORK_TYPES.map(type => (
                              <option key={type.id} value={type.id}>{type.label}</option>
                            ))}
                          </select>
                        </div>
                        
                        {/* Flower & Plant Type Selectors */}
                        <div className="space-y-3">
                          <div className="bg-slate-800 rounded-xl p-3 border border-slate-700">
                            <div className="flex items-center justify-between gap-2 mb-2">
                              <div className="flex items-center gap-2 min-w-0">
                                <Flower2 size={16} className="text-pink-400 shrink-0" />
                                <span className="text-sm text-slate-300 font-medium">Flower Type</span>
                              </div>
                              <button
                                onClick={() => { handleAddFlowers(); setMobileSheetOpen(false); }}
                                disabled={isProcessing}
                                className="px-3 py-1.5 bg-pink-600 text-white text-xs font-medium rounded-lg disabled:opacity-50 shrink-0"
                              >
                                Add Flowers
                              </button>
                            </div>
                            <p className="text-[11px] text-pink-300 mb-2 truncate">
                              {FLOWER_TYPES.find((type) => type.id === selectedFlowerId)?.label}
                            </p>
                            <BotanicalSwatchGrid
                              types={FLOWER_TYPES}
                              selectedId={selectedFlowerId}
                              onSelect={setSelectedFlowerId}
                              accent="pink"
                              open={mobileSheetOpen}
                            />
                          </div>
                          <div className="bg-slate-800 rounded-xl p-3 border border-slate-700">
                            <div className="flex items-center justify-between gap-2 mb-2">
                              <div className="flex items-center gap-2 min-w-0">
                                <TreeDeciduous size={16} className="text-emerald-400 shrink-0" />
                                <span className="text-sm text-slate-300 font-medium">Plant Type</span>
                              </div>
                              <button
                                onClick={() => { handleAddPlants(); setMobileSheetOpen(false); }}
                                disabled={isProcessing}
                                className="px-3 py-1.5 bg-emerald-600 text-white text-xs font-medium rounded-lg disabled:opacity-50 shrink-0"
                              >
                                Add Plant
                              </button>
                            </div>
                            <p className="text-[11px] text-emerald-300 mb-2 truncate">
                              {PLANT_TYPES.find((type) => type.id === selectedPlantId)?.label}
                            </p>
                            <BotanicalSwatchGrid
                              types={PLANT_TYPES}
                              selectedId={selectedPlantId}
                              onSelect={setSelectedPlantId}
                              accent="emerald"
                              open={mobileSheetOpen}
                            />
                          </div>
                        </div>
                        
                        {/* Change Color Selectors */}
                        <div className="bg-slate-800 rounded-xl p-3 border border-slate-700">
                          <div className="flex items-center gap-2 mb-2">
                            <Palette size={16} className="text-violet-400" />
                            <span className="text-sm text-slate-300 font-medium">Change Color</span>
                          </div>
                          <div className="space-y-2">
                            <div>
                              <label className="text-[10px] text-slate-500 uppercase font-semibold tracking-wider mb-1 block">Target</label>
                              <select 
                                value={selectedColorTarget}
                                onChange={(e) => setSelectedColorTarget(e.target.value)}
                                className="w-full bg-slate-900 text-xs text-slate-300 border border-slate-700 rounded-lg py-2 px-2"
                              >
                                {COLOR_TARGETS.map(target => (
                                  <option key={target.id} value={target.id}>{target.label}</option>
                                ))}
                              </select>
                            </div>
                            <div>
                              <label className="text-[10px] text-slate-500 uppercase font-semibold tracking-wider mb-1 block">Color</label>
                              <div className="space-y-1.5">
                                {Array.from(new Set(COLOR_OPTIONS.map(c => c.group))).map(group => (
                                  <div key={group}>
                                    <span className="text-[9px] text-slate-500 font-medium">{group}</span>
                                    <div className="flex flex-wrap gap-1.5 mt-0.5">
                                      {COLOR_OPTIONS.filter(c => c.group === group).map(color => (
                                        <button
                                          key={color.id}
                                          onClick={() => setSelectedColor(color.id)}
                                          title={color.label}
                                          className={`w-6 h-6 rounded-full border-2 transition-all ${
                                            selectedColor === color.id
                                              ? 'border-violet-400 ring-1 ring-violet-400/50 scale-110'
                                              : 'border-slate-600'
                                          }`}
                                          style={{ backgroundColor: color.hex }}
                                        />
                                      ))}
                                    </div>
                                  </div>
                                ))}
                                <button
                                  onClick={() => setSelectedColor('custom')}
                                  className={`mt-1 w-full text-left text-[10px] px-2 py-1.5 rounded-md border transition-all ${
                                    selectedColor === 'custom'
                                      ? 'bg-violet-900/30 border-violet-500/50 text-violet-300'
                                      : 'bg-slate-900 border-slate-700 text-slate-400'
                                  }`}
                                >
                                  Custom colour...
                                </button>
                                {selectedColor === 'custom' && (
                                  <input
                                    type="text"
                                    value={customColorText}
                                    onChange={(e) => setCustomColorText(e.target.value)}
                                    placeholder="e.g. warm terracotta, pale mint"
                                    className="w-full bg-slate-900 text-xs text-slate-300 border border-violet-500/50 rounded-md py-1.5 px-2 outline-none focus:ring-1 focus:ring-violet-500/30 placeholder-slate-600"
                                  />
                                )}
                              </div>
                              {selectedColor !== 'custom' && (
                                <p className="text-[10px] text-slate-500 mt-1">
                                  {COLOR_OPTIONS.find(c => c.id === selectedColor)?.label}
                                </p>
                              )}
                            </div>
                          </div>
                        </div>

                        {/* Pin Location */}
                        <button
                          onClick={() => { setIsPlacingPin(true); setMobileSheetOpen(false); }}
                          className={`w-full py-3 px-4 rounded-xl border flex items-center justify-center gap-2 transition-all ${
                            pinLocation 
                              ? 'bg-pink-900/30 border-pink-500/50 text-pink-300' 
                              : 'bg-slate-800 border-slate-700 text-slate-300'
                          }`}
                        >
                          <MapPin size={18} />
                          <span>{pinLocation ? 'Location Set - Tap to Change' : 'Set Placement Location'}</span>
                        </button>
                      </div>
                      )}
                    </>
                  ) : is360Image ? (
                    /* Custom Tab — disabled in 360° mode */
                    <div className="bg-slate-800 border border-slate-700 rounded-xl p-4 text-center text-sm text-slate-400">
                      Custom prompts and brush masking are disabled in 360° Panorama Mode. Use the
                      Quick Actions tab to stage the room.
                    </div>
                  ) : (
                    /* Custom Tab */
                    <div className="space-y-4">
                      <p className="text-sm text-slate-400">
                        Paint over items to modify them, or describe what you want to change.
                      </p>
                      <textarea 
                        className="w-full h-32 text-sm p-3 border border-slate-700 rounded-xl bg-slate-800 text-slate-200 placeholder-slate-500 resize-none"
                        value={customPrompt}
                        onChange={(e) => setCustomPrompt(e.target.value)}
                        placeholder="Describe your request (e.g. 'remove the sofa', 'make the sky blue')..."
                      />
                      <button
                        onClick={() => { handleManualProcess(); setMobileSheetOpen(false); }}
                        disabled={isProcessing || (!hasMask && !customPrompt.trim())}
                        className={`
                          w-full py-4 rounded-xl font-semibold text-white flex items-center justify-center gap-2 transition-all
                          ${isProcessing || (!hasMask && !customPrompt.trim())
                            ? 'bg-slate-700 text-slate-500' 
                            : 'bg-orange-600 active:scale-[0.98]'
                          }
                        `}
                      >
                        {hasMask && !customPrompt.trim() ? (
                          <>Remove Selected <ArrowRight size={20} /></>
                        ) : (
                          <>Apply Edit <Wand2 size={18} /></>
                        )}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      
      {/* 360 Viewer Modal */}
      {show360Viewer && (
        <Viewer360 
          imageUrl={imageUrl} 
          onClose={() => setShow360Viewer(false)}
          title="360° Preview"
        />
      )}
    </div>
  );
};

export default Editor;
